
import { Component, signal, computed, effect, inject, ElementRef, ViewChild, ChangeDetectionStrategy, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DeviceService } from "./services/device.service";
import {
  StoreSettingsService,
  StoreSetting,
  DynamicSettingParameter,
  COMPANIES,
  BRANCHES,
  LOCATIONS,
  STORES,
  CASH_ACCOUNTS,
  SALES_INVOICE_SERIES,
  SALES_ACCOUNTS,
  DEFAULT_CUSTOMERS,
  WAREHOUSES,
  STOCK_LOCATIONS,
  PRICE_LISTS,
  TAX_CATEGORIES,
  PAYMENT_MODES,
  CURRENCIES,
  CompanyOption,
  BranchOption,
  LocationOption,
  StoreOption
} from './services/store-setting.service';
import {
  CounterSalesApiService,
  CounterSalesInvoicePayload,
  CounterSalesProductItem,
  CounterSalesSummary,
  OfflineHoldBillRecord
} from './services/counter-sales-api.service';
import { AuthService} from './services/auth.service'
import {
  AgentAuthService,
  AgentLoginRequest,
  AgentLoginResponse,
  AdminSubscribeRequest,
  AdminSubscribeResponse,
  MenuGroup,
  SubscriberModule,
  ProfileCompany,
  ProfileUserProfile
} from './services/agent-auth.service';
import {
  ProductLookupService,
  ApiProductItem,
  ProductPriceOption,
  ProductLookupRequest
} from './services/product-lookup.service';
import {
  PosPrinterService,
  PosPrinter,
  PrintJobResult,
  DeviceManagerDriver
} from './services/pos-printer.service';
import { catchError, firstValueFrom, from, map, Observable, of, switchMap } from 'rxjs';
import { EncryptionService, session_storage_keys } from './services/encryption.service';
import { ApiService } from './services/api.service';
import { Global } from './constants/global';
import { timeStamp } from 'console';

// --- Interfaces ---
export type { ProductPriceOption, PosPrinter, DeviceManagerDriver };

export interface Product {
  pr_ProductID: string
  masterID: string
  companyID: number
  subscriptionID: number
  pr_IndustryRelation: string
  pr_RelationID: string
  pr_RelationName: string
  pr_ProductTitle: string
  pr_ProductName: string
  pr_Image: string
  pr_ProductCode: string
  pr_IsEnableBarCode: boolean
  pr_IsEnableDefaultBarCode: string
  barCodeBreakups: any[]
  pr_ProductSkuID: string
  pr_IsAltUomEnabled: boolean
  alternateUoms: any[]
  pr_BaseUomID: string
  pr_BaseUOMName: string
  pr_IsProductBatch: boolean
  pr_ProductOrgin: string
  pr_HsnID: string
  pr_HSNName: string
  pr_AIScriptTagProduct: string
  pr_SearchingTag: string
  pr_ProductDefine: string
  pr_ProductDescription: string
  pr_ProductContent: string
  pr_IsDefaultPrice: boolean
  pr_IsAsset: boolean
  purchasePrice: PurchasePrice
  salesPrice: SalesPrice
  pr_IsProductAttributes: boolean
  productCategory: ProductCategory
  productBrand: ProductBrand
  productType: any
  pr_ActiveStatus: boolean
  pr_ProductVoid: boolean
  pr_CreatedAt: string
  pr_UpdatedAt: string
  pr_DefaultSalesMRPPrice:any
}
export class PurchasePrice {
  pr_PurchasePriceID: string
  pr_PurchasePriceEffectedDt: string
  pr_DefaultPurchasePrice: number
  pr_DefaultPurchaseMRPPrice: number
  pr_DefaultPurchasePriceInclTax: boolean
  pr_IsDiscountEnabledOnPurchase: boolean
  purchaseDiscountBreakups: any[]
}

export class SalesPrice {
  pr_SalesPriceID: string
  pr_SalesPriceEffectedDt: string
  pr_DefaultSalesPrice: number
  pr_DefaultSalesMRPPrice: number
  pr_DefaultSalesPriceInclTax: boolean
  pr_IsDiscountEnabledOnSales: boolean
  salesDiscountBreakups: SalesDiscountBreakup[]
}

export class SalesDiscountBreakup {
  pr_SalesDiscountID: string
  discountLevel: string
  discountType: string
  discountValue: number
  discountRemarks: any
}

export class PurchaseDiscountBreakup {
  pr_PurchaseDiscountID: string
  discountLevel: string
  discountType: string
  discountValue: number
  discountRemarks: any
}
export interface ProductCategory {
  productCategorizedID: string
  categoryID: string
  categoryName: string
}
export interface ProductBrand {
  productBrandedID: string
  brandID: string
  brandName: string
}
export class BarCodeBreakup {
  barCodeBreakupID: string
  barCodeType: string
  barCodeValue: string
  barCodeFormatID: string
  isPrimary: boolean
  remarks: string
}
interface CartItem extends Product {
  qty: number;
  unit?: string; // Unit of measure (e.g., 'ea', 'kg', 'box')
  discount?: number; // Total discount amount for this line
  note?: string;
  itemCsiId?: string;
}
// interface CartItem extends Product {
//   qty: number;
//   unit?: string; // Unit of measure (e.g., 'ea', 'kg', 'box')
//   discount?: number; // Total discount amount for this line
//   note?: string;
//   itemCsiId?: string;
//   barcode?: string;
//   salesPrice?: number;
//   wholesalePrice?: number;
//   priceOptions?: ProductPriceOption[];
//   selectedPriceOption?: string;
// }

interface StockRecord {
  location: string;
  onHand: number;
  reserved: number;
}

interface OrderItem {
  name: string;
  qty: number;
  price: number;
}

interface OnlineOrder {
  id: string;
  customer: string;
  time: Date;
  items: number;
  total: number;
  status: 'Pending' | 'Ready' | 'Completed';
  platform: 'UberEats' | 'DoorDash' | 'Web';
  details?: OrderItem[];
}

interface Customer {
  id: string;
  name: string;
  phone: string;
  email: string;
  balance: number;
  points: number;
  initials: string;
  segment: 'VIP' | 'Regular' | 'New';
  registeredAt: Date;
  // Counter Invoice Party Details
  partyCode?: string;
  partyState?: string;
  partyTaxNumber?: string;
  couponCode?: string;
  membershipId?: string;
  loyaltyPoints?: string;
  shippingAddressId?: string;
  shippingLabelName?: string;
  shippingReceiverName?: string;
  billingAddressId?: string;
  billingLabelName?: string;
  billingReceiverName?: string;
}

interface HeldBill {
  id: string;
  ticketRef: string;
  time: Date;
  customer: Customer | null;
  items: CartItem[];
  total: number;
  offlineHoldRecordId?: string;
}

interface VoidedBill {
  id: string;
  originalRef: string;
  time: Date;
  customer: Customer | null;
  items: CartItem[];
  total: number;
  reason: string;
}

interface DiscountRule {
    type: 'percent' | 'amount';
    value: number;
}

// New Interface for Completed Transactions
interface Transaction {
  id: string;
  date: Date;
  items: CartItem[];
  grossTotal: number;
  totalItemDiscount: number;
  subtotal: number; // Net subtotal after item discounts
  discount: number; // Global bill discount
  tax: number;
  total: number; // Final amount paid
  method: string;
  customer: Customer | null;
  cashSalesInvoiceRef?: string;
  cashAccount?: string;
  salesAccount?: string;
  warehouse?: string;
  stockLocation?: string;
  priceList?: string;
  taxCategory?: string;
  currency?: string;
  storeId?: string;
  storeName?: string;
  // Counter Sales Invoice Full API Snapshot
  csiPayload?: CounterSalesInvoicePayload;
  csiApiStatus?: 'SUCCESS' | 'OFFLINE_STORED' | 'PENDING';
  csiEndpoint?: string;
  transportationCharges?: number;
  roundingOff?: number;
}

// --- Settings Interfaces ---
interface BusinessType {
  id: string;
  name: string;
  desc: string;
  icon: string;
  colorClass: string; // Tailwind class for icon background
}

interface StaffUser {
  id: string;
  fullName: string;
  role: 'Manager' | 'Cashier' | 'Trainee' | 'Agent Admin';
  accessCode: string;
  defaultStoreId: string;
  isActive: boolean;
  initials: string;
  shiftStatus: 'On Shift' | 'Off Shift';
  permissions: {
    canVoid: boolean;
    canDiscount: boolean;
    canOpenDrawer: boolean;
    canEditSettings: boolean;
  };
}

// --- Mock Data ---
const MOCK_STAFF_USERS: StaffUser[] = [
  { id: 'USER-001', fullName: 'John Doe', role: 'Manager', accessCode: '654321', defaultStoreId: 'store-01', isActive: true, initials: 'JD', shiftStatus: 'Off Shift', permissions: { canVoid: true, canDiscount: true, canOpenDrawer: true, canEditSettings: true } },
  { id: 'USER-002', fullName: 'Jane Smith', role: 'Cashier', accessCode: '111111', defaultStoreId: 'store-01', isActive: true, initials: 'JS', shiftStatus: 'Off Shift', permissions: { canVoid: false, canDiscount: true, canOpenDrawer: true, canEditSettings: false } },
  { id: 'USER-003', fullName: 'Peter Jones', role: 'Cashier', accessCode: '222222', defaultStoreId: 'store-02', isActive: true, initials: 'PJ', shiftStatus: 'Off Shift', permissions: { canVoid: false, canDiscount: true, canOpenDrawer: true, canEditSettings: false } },
];

const BUSINESS_TYPES: BusinessType[] = [
  { id: 'retail', name: 'Retail POS', desc: 'Standard checkout for shops and boutiques.', icon: 'storefront', colorClass: 'bg-blue-500' },
  { id: 'restaurant', name: 'Restaurant POS', desc: 'Table management, courses, and kitchen printing.', icon: 'restaurant', colorClass: 'bg-orange-500' },
  { id: 'foodcourt', name: 'Food Court POS', desc: 'Quick service, pagers, and high volume.', icon: 'fastfood', colorClass: 'bg-red-500' },
  { id: 'pharma', name: 'Pharma POS', desc: 'Batch tracking, expiry alerts, and generics.', icon: 'medication', colorClass: 'bg-green-500' },
  { id: 'garments', name: 'Garments POS', desc: 'Size/Color matrix and fashion variants.', icon: 'checkroom', colorClass: 'bg-purple-500' },
  { id: 'drive', name: 'Drive Through', desc: 'Lane management and speed timers.', icon: 'directions_car', colorClass: 'bg-yellow-500' },
];

const CATALOG_DATA: Record<string, any> = {};

const INITIAL_STOCK: Record<string, StockRecord[]> = {
  "PARA-30339": [
    { location: "HOUSTON-15", onHand: 350, reserved: 10 }
  ],
  "000113": [
    { location: "HOUSTON-15", onHand: 18, reserved: 2 },
    { location: "AUSTIN-02", onHand: 9, reserved: 1 },
  ],
  "890123": [
    { location: "WAREHOUSE", onHand: 120, reserved: 10 },
  ],
   "111001": [
    { location: "WAREHOUSE", onHand: 8, reserved: 0 },
  ],
};

const MOCK_ORDERS: OnlineOrder[] = [
  { 
    id: "#WEB-1023", 
    customer: "Alice M.", 
    time: new Date(Date.now() - 1000 * 60 * 5), 
    items: 3, 
    total: 45.50, 
    status: 'Pending', 
    platform: 'Web',
    details: [
      { name: "Organic Almonds 1kg", qty: 2, price: 12.50 },
      { name: "USB-C Cable 2m", qty: 1, price: 9.99 },
      { name: "Priority Shipping", qty: 1, price: 10.51 }
    ]
  },
  { 
    id: "#UE-9921", 
    customer: "Robert F.", 
    time: new Date(Date.now() - 1000 * 60 * 12), 
    items: 1, 
    total: 18.00, 
    status: 'Ready', 
    platform: 'UberEats',
    details: [
      { name: "Espresso Beans 500g", qty: 1, price: 18.00 }
    ]
  },
  { 
    id: "#DD-8811", 
    customer: "Sarah L.", 
    time: new Date(Date.now() - 1000 * 60 * 25), 
    items: 8, 
    total: 156.20, 
    status: 'Pending', 
    platform: 'DoorDash',
    details: [
      { name: "Cotton T-Shirt (White)", qty: 4, price: 15.00 },
      { name: "Denim Jeans (Slim)", qty: 1, price: 65.00 },
      { name: "Socks Pack", qty: 3, price: 10.40 }
    ]
  },
  { 
    id: "#WEB-1022", 
    customer: "Mike Ross", 
    time: new Date(Date.now() - 1000 * 60 * 45), 
    items: 2, 
    total: 200.00, 
    status: 'Completed', 
    platform: 'Web',
    details: [
      { name: "Black Quilted Bag", qty: 1, price: 200.00 }
    ]
  },
];

const MOCK_CUSTOMERS: Customer[] = [
  { id: "006948b0-aef1-4447-854d-69892717f03f", name: "NEW BHAGYALAXMI", phone: "PartyContact-05", email: "bhagyalaxmi@example.com", balance: 0.00, points: 1500, initials: "NB", segment: 'VIP', registeredAt: new Date('2022-01-10T10:00:00Z'), partyCode: "P-171", partyState: "KNR", couponCode: "partyCoupon-01", membershipId: "Member-01", loyaltyPoints: "points-001" },
  { id: "CUST-001", name: "Karen Berg", phone: "(555) 010-9988", email: "karen@example.com", balance: 0.00, points: 1250, initials: "KB", segment: 'VIP', registeredAt: new Date('2022-03-15T10:00:00Z') },
  { id: "CUST-002", name: "James Smith", phone: "(555) 012-3344", email: "james.s@example.com", balance: 15.50, points: 300, initials: "JS", segment: 'Regular', registeredAt: new Date('2023-01-20T11:30:00Z') },
  { id: "CUST-003", name: "Maria Garcia", phone: "(555) 015-5566", email: "maria.g@example.com", balance: -5.00, points: 850, initials: "MG", segment: 'Regular', registeredAt: new Date('2022-08-01T14:00:00Z') },
  { id: "CUST-004", name: "Robert Johnson", phone: "(555) 018-7722", email: "bob.j@example.com", balance: 0.00, points: 50, initials: "RJ", segment: 'New', registeredAt: new Date('2023-10-10T18:45:00Z') },
];

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app.component.html',
  host: {
    '(window:keydown)': 'handleGlobalKeydown($event)'
  }
})
export class AppComponent implements OnInit, OnDestroy {
  // --- Login State ---
  isLoggedIn = signal(false);
  isAuthenticating = signal(false);
  loginError = signal(false);
  isLoading= signal(false);
  loginSuccess = signal(false);
  loginErrorMessage = signal<string | null>(null);
  currentUser = signal<StaffUser | null>(null);
  selectedUserForLogin = signal<StaffUser | null>(null);
  locations=[];
  parties=[];
  addressList=[];
  branchAddressList=[];
  accountList=[];
  taxTypesList=[];
  taxRatesList=[];
  // Login Mode: 'agent-api' (Finobooks Agent API) | 'staff-pin' (Staff PIN Access)
  loginMode = signal<'agent-api' | 'staff-pin'>('agent-api');
  partyPriceList=[];
  customers = signal<Customer[]>(MOCK_CUSTOMERS);
  // Finobooks Agent API Login Form (https://agent.finobooks.com/api/Agent/Login)
  agentUsername = signal<string>('test011@litops.net');
  agentPassword = signal<string>('Test@12345');
  showAgentPassword = signal<boolean>(false);
  showAdvancedTelemetry = signal<boolean>(false);
  storeSettings = signal<any>([]);
  // Device & Telemetry Data
  agentDeviceName = signal<string>('Windows');
  agentIpAddress = signal<string>('Unknown');
  agentCountryName = signal<string>('Unknown');
  agentCurrentLocation = signal<string>('Unknown');
  agentOsDetails = signal<string>('Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/154.0.0.0 Safari/537.36');
  agentLoginDateTime = signal<string>(new Date().toISOString());
  agentDeviceTime = signal<string>(new Date().toISOString());

  // Agent Auth & Profile Modals, Tabs & Feedback
  isAgentAuthInspectorOpen = signal<boolean>(false);
  activeInspectorTab = signal<'auth' | 'profile'>('auth');
  activeProfileDrawerTab = signal<'profile' | 'modules' | 'menus' | 'companies'>('profile');
  agentAuthSuccessNotice = signal<string | null>(null);
  
  // Login Form (PIN)
  accessCode = signal('');
  selectedStore = signal('store-01');
  enableCashOpening = signal(false);
  openingFloat = signal<number | null>(null);
  
  // Session Info
  sessionStartTime = signal<Date>(new Date());
  shiftId = signal('SH-' + Math.floor(Math.random() * 10000));
  
  stores = [
    { id: 'store-01', name: 'Store 001 - Main St (HQ)' },
    { id: 'store-02', name: 'Store 002 - West Mall' },
    { id: 'store-03', name: 'Store 003 - Airport Kiosk' }
  ];

  // --- Network & Offline State ---
  isOnline = signal(navigator.onLine);
  lastSyncTime = signal<Date>(new Date());
  isSyncing = signal(false);
  syncStatus = signal<'idle' | 'syncing' | 'success' | 'error'>('idle');
  syncLog = signal<string[]>([]);
  stockMap = signal<Record<string, StockRecord[]>>(INITIAL_STOCK);
  
  // Queue for offline transactions
  offlineQueue = signal<any[]>([]);

  // --- App State ---
  isDarkMode = signal(true);
  activeTab = signal<'lines' | 'payments' | 'online'>('lines');
 // cart = signal<CartItem[]>([]);
 cart = signal<any[]>([]);
  selectedLineIndex = signal<number>(-1);
  searchQuery = signal('');
  
  onlineOrders = signal<OnlineOrder[]>(MOCK_ORDERS);
  expandedOrderId = signal<string | null>(null);

  // --- Product Catalog State ---
  productCatalog = signal<Record<string, Product>>(CATALOG_DATA);

  // Layout State
  isRightPanelCollapsed = signal(true);
  
  // Profile Drawer State
  isProfileDrawerOpen = signal(false);

  // Settings Modal State
  isSettingsModalOpen = signal(false);
  settingsTab = signal<'store'|'business'|'product'|'barcode'|'stock'|'staff'|'system'|'customers'|'printer'>('business');
  selectedBusinessType = signal('retail');
  readonly businessTypes = BUSINESS_TYPES;

  // --- Store Settings Service & State ---
  storeSettingsService = inject(StoreSettingsService);
  counterSalesApi = inject(CounterSalesApiService);
  agentAuth = inject(AgentAuthService);
  authService = inject(AuthService);
  productLookup = inject(ProductLookupService);
  posPrinter = inject(PosPrinterService);

  // --- Dynamic Inventory Product & Catalog State ---
  isSearchingProduct = signal<boolean>(false);
  productFeedback = signal<{ text: string; type: 'success' | 'info' | 'warning' } | null>(null);
  private feedbackTimeout: any = null;

  catalogPageNumber = signal<number>(1);
  catalogPageSize = signal<number>(10);
  catalogSearchQuery = signal<string>('');
  catalogSelectedCategory = signal<string | null>(null);
  catalogSelectedBrand = signal<string | null>(null);

  // Price Option Modal State (for binding Sales Price / Tier)
  isPriceOptionModalOpen = signal<boolean>(false);
  priceOptionSelectedItem = signal<CartItem | null>(null);
  priceOptionSelectedLineIndex = signal<number>(-1);
  
  // Master Dropdown Options
  readonly companiesList = COMPANIES;
  readonly cashAccountsList = CASH_ACCOUNTS;
  readonly salesInvoiceSeriesList = SALES_INVOICE_SERIES;
  readonly salesAccountsList = SALES_ACCOUNTS;
  readonly defaultCustomersList = DEFAULT_CUSTOMERS;
  readonly warehousesList = WAREHOUSES;
  readonly stockLocationsList = STOCK_LOCATIONS;
  readonly priceListsList = PRICE_LISTS;
  readonly taxCategoriesList = TAX_CATEGORIES;
  readonly paymentModesList = PAYMENT_MODES;
  readonly currenciesList = CURRENCIES;
  settingPartyState = signal<string>('');
  settingBranchState = signal<string>('');
// Store Settings Form Signals
  storeSettingId = signal<string | null>('SET-001'); // null = new, string = editing
  masterId = signal<string>('MST-88201');
  subscriptionId = signal<string>('SUB-OCTO-2026');
  settingCompany = signal<string>('COMP-01');
  settingBranch = signal<string>('');
  settingBranchName = signal<string>('');
  //settingBranchState = signal<string>('HYD');
  settingLocation = signal<string>('');
  settingLocationName = signal<string>('');
  settingStore = signal<string>('store-01');
  settingStoreName = signal<string>('Store 001 - Main St (HQ)');
  settingDefaultCashAccount = signal<string>('');
  settingDefaultCashAccountID= signal<string>('');
  settingCashSalesInvoice = signal<string>('');
  settingCashSalesInvoiceID = signal<string>('');
  settingDefaultSalesAccount = signal<string>('');
  settingDefaultSalesAccountID = signal<string>('');
  settingDefaultCustomer = signal<string>('');
  settingDefaultCustomerID = signal<string>('');
  settingDefaultWarehouse = signal<string>('');
  settingDefaultWarehouseID = signal<string>('');
  settingDefaultStockLocation = signal<string>('');
  settingDefaultStockLocationID = signal<string>('');
  settingDefaultPriceList = signal<string>('');
  settingDefaultPriceListID = signal<string>('');
  settingDefaultTaxCategory = signal<string>('');
  settingDefaultTaxCategoryID = signal<string>('');
  settingDefaultPaymentMode = signal<string>('Cash');
  settingDefaultCurrency = signal<string>('USD');

  // Extended Header Binding Parameters under Store Setting
  settingPassKey = signal<string>('PK-992014-X');
  settingRoleId = signal<string>('ROLE-MGR-01');
  settingSalesAccountName = signal<string>('');
  ettingSalesAccountID = signal<string>('');
  settingStockControlAccountID = signal<string>('');
  settingStockControlAccountName = signal<string>('');
  settingSalesCashAccountName = signal<string>('');
  settingSalesCashAccountID = signal<string>('');
  settingCounterOperatorID = signal<string>('operator-01');
  settingCounterOperatorName = signal<string>('Operator-ABC');
  settingIsCreditSales = signal<boolean>(true);
  settingInvoiceRequest = signal<boolean>(true);
  settingIsEnableStorageBin = signal<boolean>(false);
  settingIsEnableTransportation = signal<boolean>(true);
  settingIsEnableOtherInfo = signal<boolean>(true);
  settingIsDiscountOnSummary = signal<boolean>(false);
  settingTaxTypeID = signal<string>('gst');
  settingTaxTypeCode = signal<string>('gst');
  settingTaxRateID = signal<string>('taa-11');
  settingTaxRateValue = signal<number>(87.9);
  settingTaxRateCode = signal<string>('ta-678');
  settingPaymentTerms = signal<string>('assa');
  settingShippingAddressId = signal<string>('shipadd-12');
  settingShippingLabelName = signal<string>('shiplable-12');
  settingShippingReceiverName = signal<string>('shipRe-12');
  settingBillingAddressId = signal<string>('billad-12');
  settingBillingLabelName = signal<string>('billlab-12');
  settingBillingReceiverName = signal<string>('billre-12');
  settingTransportationCharges = signal<number>(65);

  // Counter Sales API status and inspector modal
  isCsiInspectorModalOpen = signal(false);
  lastCsiPayload = signal<any | null>(null);
  apiToastMessage = signal<{ text: string; type: 'success' | 'info' | 'warn' | 'error' } | null>(null);
  offlineHoldInvoicesCount = computed(() => this.counterSalesApi.offlineHeldBills().length);

  settingIsActive = signal<boolean>(true);
  settingParameters = signal<DynamicSettingParameter[]>([
    { key: 'allow_negative_inventory', label: 'Allow Negative Inventory', value: 'false' },
    { key: 'auto_print_receipt', label: 'Auto Print Thermal Receipt', value: 'true' },
    { key: 'pos_counter_number', label: 'Terminal Counter ID', value: 'POS-TERM-01' }
  ]);

  // Dynamic Parameter Add inputs
  newParamKey = signal('');
  newParamLabel = signal('');
  newParamValue = signal('');

  // UI state for Store Settings
  storeSettingFeedback = signal<{ type: 'success' | 'error'; message: string; code?: string } | null>(null);
  isSavingStoreSetting = signal(false);
  isStoreConfiguredAlertOpen = signal(false);

  // Cascading drop-down options derived using signals
  availableBranches = computed(() => this.storeSettingsService.getBranches(this.settingCompany()));
  availableLocations = computed(() => this.storeSettingsService.getLocations(this.settingBranch()));
  availableStores = computed(() => this.storeSettingsService.getStores(this.settingLocation()));

  // Active Store Configured Status
  activeStoreConfig = computed(() => this.storeSettingsService.getSettingForStore(this.selectedStore()));
  isCurrentStoreConfigured = computed(() => !!this.activeStoreConfig());
  allStoreSettings = computed(() => this.storeSettingsService.storeSettings());
  isEditMode = computed(() => !!this.storeSettingId());

  // Store Token State
  storeTokens = signal<Record<string, { token: string | null; expires: number | null; }>>({});
  tokenExpiryCounters = signal<Record<string, number>>({});
  deviceRegistrationToken = signal('');

  // --- Staff Management State ---
  staffUsers = signal<StaffUser[]>(MOCK_STAFF_USERS);
  isStaffModalOpen = signal(false);
  editingStaffUser = signal<StaffUser | null>(null);
  staffUserForm = signal<Partial<StaffUser>>({});
  readonly staffRoles: StaffUser['role'][] = ['Manager', 'Cashier', 'Trainee', 'Agent Admin'];
  readonly defaultPermissionsByRole: Record<StaffUser['role'], StaffUser['permissions']> = {
    'Agent Admin': { canVoid: true, canDiscount: true, canOpenDrawer: true, canEditSettings: true },
    Manager: { canVoid: true, canDiscount: true, canOpenDrawer: true, canEditSettings: true },
    Cashier: { canVoid: false, canDiscount: true, canOpenDrawer: true, canEditSettings: false },
    Trainee: { canVoid: false, canDiscount: false, canOpenDrawer: false, canEditSettings: false },
  };

  // Barcode Printing State
  barcodeSelectedItemIndex = signal<number | null>(null);

  // Z-Report State
  isZReportOpen = signal(false);
  zReportTab = signal<'summary' | 'transactions'>('summary');
  sessionTransactions = signal<Transaction[]>([]);
  selectedTransaction = signal<Transaction | null>(null);

  // Stock / Product Lookup State
  isStockModalOpen = signal(false);
  stockSearchQuery = signal('');
  stockSelectedCategory = signal<string | null>(null);
  stockSelectedIndex = signal(0); // For keyboard navigation in stock list

  // Stock Alert State
  isLowStockAlertEnabled = signal(false);
  lowStockThreshold = signal<number>(10);

  // NumPad / Custom Amount State
  isNumPadOpen = signal(false);
  numPadValue = signal('');

  // Discount Modal State
  isDiscountModalOpen = signal(false);
  discountModalTab = signal<'line' | 'global'>('global');
  discountModalType = signal<'percent' | 'amount'>('percent');
  discountModalValue = signal('0');
  globalDiscount = signal<DiscountRule | null>(null);

  // Receipt State
  isReceiptVisible = signal(false);
  isPrinting = signal(false);
  receiptDetails = signal<Transaction | null>(null);

  // Set Qty Modal State
  isQtyModalOpen = signal(false);
  qtyModalValue = signal('');

  // Loyalty/Customer State

  currentCustomer = signal<Customer | null>(null); // Defaults to null (Walk-in)
  isLoyaltyModalOpen = signal(false);
  loyaltyQuery = signal('');
  
  // --- Customer Management State ---
  isCustomerModalOpen = signal(false);
  editingCustomer = signal<Customer | null>(null);
  customerForm = signal<Partial<Customer>>({});
  customerListFilter = signal('');
  customerListSegmentFilter = signal<'All' | Customer['segment']>('All');
  customerListSort = signal<'name' | 'points' | 'newest'>('name');
  selectedCustomerForDetail = signal<Customer | null>(null);

  // Unit Modal State
  isUnitModalOpen = signal(false);
  readonly availableUnits = ['ea', 'box', 'case', 'kg', 'g', 'lb', 'oz', 'm', 'ft', 'pack'];

  // Gift Card Modal State
  isGiftCardModalOpen = signal(false);
  giftCardMode = signal<'issue' | 'redeem'>('issue');
  giftCardCode = signal('');
  giftCardAmount = signal<number>(0);

  // Hold Bill / Tab State
  heldBills = signal<HeldBill[]>([]);
  isHeldBillsModalOpen = signal(false);
  currentTabId = signal<string | null>(null); // If set, we are editing this existing tab
  currentTabRef = signal<string | null>(null);
  selectedHeldBillIndex = signal<number>(0); // For keyboard nav
  
  // Void State
  voidedBills = signal<VoidedBill[]>([]);
  isVoidModalOpen = signal(false);
  selectedVoidIndex = signal(0);

  // Scanner Buffer
  private scanBuffer = '';
  private lastKeyTime = 0;
  private syncInterval: any;
  private tokenInterval: any;

  // Computed State
  
  // 1. Bill Subtotal (Sum of all lines, after line-level discounts)
  // 1. Bill Subtotal (Sum of all lines, after line-level discounts)
  subtotal = computed(() => this.cart().reduce((acc, item) => { // Renamed from billSubtotal
    const lineTotal = (item.pr_DefaultSalesMRPPrice * item.qty) - (item.discount || 0);
    return acc + lineTotal;
  }, 0));
  
  // 2. Calculate Global Discount Amount
  globalDiscountAmount = computed(() => {
      const rule = this.globalDiscount();
      if (!rule) return 0;
      
      const sub = this.subtotal(); // Updated reference
      if (rule.type === 'amount') {
          return rule.value;
      } else {
          return sub * (rule.value / 100);
      }
  });

  // 3. Taxable Amount (Subtotal - Global Discount)
  // taxableAmount = computed(() => {
  //     return Math.max(0, this.subtotal() - this.globalDiscountAmount()); // Updated reference
  // });
  taxableAmount = computed(() => this.cart().reduce((acc, i) => acc + Number(i.csiProductTotalValue || 0), 0));

  // 4. Tax
  tax = computed(() => this.taxableAmount() * 0.0625); // 6.25% Tax

  // --- Counter Sales Summary & POS Calculations ---
  totalCartQty = computed(() => this.cart().reduce((acc, i) => acc + (i.qty > 0 ? i.qty : 0), 0));
  
  // Basic Gross Amount before line discounts
  totalBasicAmount = computed(() => 
    this.cart().reduce((acc, i) => {
      const price = Number(i.csiProductGrossValue ||  0);
      const qty = Number(i.qty || i.csiProductQty || 0);
      return acc + (price * qty);
    }, 0)
  );

  // Line item discounts sum
  totalItemDiscounts = computed(() => this.cart().reduce((acc, i) => acc + Number(i.csiProductDiscountValue || 0), 0));

  // Total discount amount (line discounts + global summary discount)
  // totalDiscountAmount = computed(() => this.totalItemDiscounts() + this.globalDiscountAmount());

  totalDiscountAmount = computed(() => this.cart().reduce((acc, i) => acc + Number(i.csiProductGrossDiscountValue || 0), 0));
  
  // Net amount before rounding (Subtotal + Tax + Transportation)
  // netAmountBeforeRounding = computed(() => {
  //   const trans = this.settingIsEnableTransportation() ? this.settingTransportationCharges() : 0;
  //   return this.taxableAmount() + this.tax() + trans;
  // });
  netAmountBeforeRounding = computed(() => 
    this.cart().reduce((acc, i) => acc + Number(i.csiProductTotalValue || 0), 0)
  );
 
  // Rounding off amount to nearest integer (or 0.50 interval)
  roundingOffAmount = computed(() => {
    const raw = this.netAmountBeforeRounding();
    const rounded = Math.round(raw);
    return parseFloat((rounded - raw).toFixed(2));
  });

  // Total Net Payable Amount
  totalNetPayableAmount = computed(() => {
    return Math.round(this.netAmountBeforeRounding());
  });

  // 5. Total Due (Binds to final payable amount)
  totalDue = computed(() => this.totalNetPayableAmount());
  
  // 6. Points Earned (Derived)
  pointsEarned = computed(() => Math.floor(this.totalDue()));

  totalLines = computed(() => this.cart().length);
  
  activeUsers = computed(() => this.staffUsers().filter(u => u.isActive));
  
  // Derived Loyalty Search Results
  filteredCustomers = computed(() => {
    const q = this.loyaltyQuery().toLowerCase();
    if (!q) return this.customers();
    return this.customers().filter(c => 
      c.name.toLowerCase().includes(q) || 
      c.phone.includes(q) || 
      c.id.toLowerCase().includes(q)
    );
  });

  // Enhanced Customer Management List
  filteredAndSortedCustomers = computed(() => {
    let customers = [...this.customers()];
    const query = this.customerListFilter().toLowerCase();
    const segment = this.customerListSegmentFilter();
    const sort = this.customerListSort();

    // Filter by Segment
    if (segment !== 'All') {
      customers = customers.filter(c => c.segment === segment);
    }

    // Filter by Search Query
    if (query) {
      customers = customers.filter(c => 
        c.name.toLowerCase().includes(query) || 
        c.phone.includes(query) ||
        c.email.toLowerCase().includes(query)
      );
    }

    // Sort
    customers.sort((a, b) => {
      if (sort === 'points') {
        return b.points - a.points;
      }
      if (sort === 'newest') {
        return b.registeredAt.getTime() - a.registeredAt.getTime();
      }
      // Default to name sort
      return a.name.localeCompare(b.name);
    });

    return customers;
  });
  
  selectedCustomerTransactions = computed(() => {
    const selected = this.selectedCustomerForDetail();
    if (!selected) return [];
    return this.sessionTransactions().filter(t => t.customer?.id === selected.id);
  });

  // Derived Stock/Catalog Results
// Derived Stock/Catalog Results
stockCatalog = computed<(Product & { totalStock: number; })[]>(() => {
  const query = this.stockSearchQuery().toLowerCase();
  const category = this.stockSelectedCategory();
  
  // 1. Get all items
  let items: Product[] = Object.values(this.productCatalog());

  // 2. Filter by Category
  if (category) {
      // FIX: Add explicit type to filter predicate parameter to ensure correct type inference.
      items = items.filter((i: Product) => i?.productCategory?.categoryID === category);
  }

  // 3. Filter by Query
  if (query) {
    // FIX: Add explicit type to filter predicate parameter to ensure correct type inference.
    items = items.filter((i: Product) => 
        i.pr_ProductName.toLowerCase().includes(query) || 
        i.pr_ProductCode.toLowerCase().includes(query) ||
        i.pr_ProductTitle.toLowerCase().includes(query)
    );
}
  
  // 4. Enrich with Stock Data for Display
  // Note: Explicitly type 'item' to resolve type inference issue within computed.
  return items.map((item: Product) => ({
    ...item,
    totalStock: this.getTotalStock(item?.pr_ProductSkuID)
}));
});

stockCategories = computed(() => {
  const cats = new Set(Object.values(this.productCatalog()).map((i: Product) => i.productCategory?.categoryID).filter(c => !!c));
  return Array.from(cats) as string[];
});

// Barcode Label Selected Item
barcodeSelectedItem = computed(() => {
  const idx = this.barcodeSelectedItemIndex();
  const currentCart = this.cart();
  if (idx !== null && currentCart[idx]) {
      return currentCart[idx];
  }
  return null;
});

// Helper for Product Catalog view
productsForCatalogView = computed(() => {
  // FIX: Add explicit type to map callback parameter to ensure correct type inference.
  return Object.values(this.productCatalog()).map((p: Product) => ({
    ...p,
    totalStock: this.getTotalStock(p?.pr_ProductSkuID)
  }));
});

  // --- Z-REPORT Computed States (Replacing Template Arrow Functions) ---
  totalSessionSales = computed(() => this.sessionTransactions().reduce((acc, trx) => acc + trx.total, 0));
  totalVoidedAmount = computed(() => this.voidedBills().reduce((acc, bill) => acc + bill.total, 0));

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('scannerInput') scannerInput!: ElementRef<HTMLInputElement>;
  @ViewChild('loyaltyInput') loyaltyInput!: ElementRef<HTMLInputElement>;
  @ViewChild('giftCardInput') giftCardInput!: ElementRef<HTMLInputElement>;
  @ViewChild('tabInput') tabInput!: ElementRef<HTMLInputElement>; 
  @ViewChild('stockInput') stockInput!: ElementRef<HTMLInputElement>; // New for Stock Modal

  constructor(private deviceService: DeviceService,private encryptionService: EncryptionService,
    private apiService: ApiService,
  ) {
    // Initial Seed with Rich Demo List - Will be overwritten by localStorage if exists
    const savedCart = localStorage.getItem('pos_cart');
    if (!savedCart) {
        this.addToCart(this.productCatalog()['890123']);
        this.addToCart(this.productCatalog()['123456']);
        this.addToCart(this.productCatalog()['555001']);
    }
    
    // Set default customer for demo purposes
    this.currentCustomer.set(this.customers()[0]);
    
    // Seed transactions for Z-Report demo
    //this.seedMockTransactions();

    // Theme Effect
    effect(() => {
      const isDark = this.isDarkMode();
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    });

    // --- Persistence Effects ---
    effect(() => { localStorage.setItem('pos_cart', JSON.stringify(this.cart())); });
    effect(() => { localStorage.setItem('pos_held_bills', JSON.stringify(this.heldBills())); });
    effect(() => { localStorage.setItem('pos_voided_bills', JSON.stringify(this.voidedBills())); });
    effect(() => { localStorage.setItem('pos_stock', JSON.stringify(this.stockMap())); });
    effect(() => { localStorage.setItem('pos_offline_queue', JSON.stringify(this.offlineQueue())); });
    effect(() => { localStorage.setItem('pos_business_type', this.selectedBusinessType()); });
    effect(() => { localStorage.setItem('pos_product_catalog', JSON.stringify(this.productCatalog())); });
    effect(() => { localStorage.setItem('pos_staff_users', JSON.stringify(this.staffUsers())); });
    effect(() => { localStorage.setItem('pos_low_stock_alert_enabled', JSON.stringify(this.isLowStockAlertEnabled())); });
    effect(() => { localStorage.setItem('pos_low_stock_threshold', JSON.stringify(this.lowStockThreshold())); });
  }
  
  // seedMockTransactions() {
  //     const t1: Transaction = {
  //        id: 'TRX-0041', date: new Date(Date.now() - 1000 * 60 * 15), total: 45.50, method: 'Cash',
  //        grossTotal: 42.00, totalItemDiscount: 0,
  //        subtotal: 42.00, tax: 3.50, discount: 0,
  //        items: [{ sku: '890123', name: 'Organic Almonds', price: 12.50, qty: 2, unit:'ea', rep:'SYS' }, { sku: '555001', name: 'Espresso Beans', price: 18.00, qty: 1, unit:'box', rep:'SYS' }],
  //        customer: this.customers()[0]
  //     };
  //     const t2: Transaction = {
  //        id: 'TRX-0042', date: new Date(Date.now() - 1000 * 60 * 5), total: 128.50, method: 'Card Payment',
  //        grossTotal: 120.00, totalItemDiscount: 0,
  //        subtotal: 120.00, tax: 8.50, discount: 0,
  //        items: [{ sku: '888002', name: 'Running Shoes', price: 120.00, qty: 1, unit:'ea', rep:'SYS' }],
  //        customer: this.customers()[1]
  //     };
  //     const t3: Transaction = {
  //        id: 'TRX-0043', date: new Date(Date.now() - 1000 * 60 * 1), total: 15.99, method: 'Card Payment',
  //        grossTotal: 15.00, totalItemDiscount: 0,
  //        subtotal: 15.00, tax: 0.99, discount: 0,
  //        items: [{ sku: '111001', name: 'Cotton T-Shirt', price: 15.00, qty: 1, unit:'ea', rep:'SYS' }],
  //        customer: null
  //     };
  //     this.sessionTransactions.set([t3, t2, t1]);
  //     this.selectedTransaction.set(t3);
  // }

  ngOnInit() {
      this.loadLocalData();

      
      window.addEventListener('online', () => this.handleConnectionChange(true));
      window.addEventListener('offline', () => this.handleConnectionChange(false));

      this.syncInterval = setInterval(() => {
          this.performBackgroundSync(false);
      }, 60000); 

      // Interval to manage token countdowns and expiration
      this.tokenInterval = setInterval(() => {
        const now = Date.now();
        const currentTokens = this.storeTokens();
        const newCounters: Record<string, number> = {};

        for (const storeId in currentTokens) {
          const tokenInfo = currentTokens[storeId];
          if (tokenInfo && tokenInfo.expires) {
            if (now >= tokenInfo.expires) {
              // Token expired, clear it
              this.storeTokens.update(tokens => {
                const newTokens = {...tokens};
                newTokens[storeId] = { token: null, expires: null };
                return newTokens;
              });
            } else {
              // Token is active, update its countdown timer
              newCounters[storeId] = Math.round((tokenInfo.expires - now) / 1000);
            }
          }
        }
        this.tokenExpiryCounters.set(newCounters);
      }, 1000);
  }

  ngOnDestroy() {
      if (this.syncInterval) clearInterval(this.syncInterval);
      if (this.tokenInterval) clearInterval(this.tokenInterval);
      window.removeEventListener('online', () => {});
      window.removeEventListener('offline', () => {});
  }

  handleConnectionChange(isOnline: boolean) {
      this.isOnline.set(isOnline);
      if (isOnline) {
          this.processOfflineQueue();
          this.performBackgroundSync(false);
      }
  }

  loadLocalData() {
      const savedCart = localStorage.getItem('pos_cart');
      if (savedCart) { try { this.cart.set(JSON.parse(savedCart)); } catch(e) { console.error('Error loading cart', e); } }

      const savedHeld = localStorage.getItem('pos_held_bills');
      if (savedHeld) { try { this.heldBills.set(JSON.parse(savedHeld)); } catch(e) { console.error('Error loading held bills', e); } }

      const savedVoid = localStorage.getItem('pos_voided_bills');
      if (savedVoid) { try { this.voidedBills.set(JSON.parse(savedVoid)); } catch(e) { console.error('Error loading voids', e); } }

      const savedQueue = localStorage.getItem('pos_offline_queue');
      if (savedQueue) { try { this.offlineQueue.set(JSON.parse(savedQueue)); } catch(e) {} }

      const savedStock = localStorage.getItem('pos_stock');
      if (savedStock) { try { this.stockMap.set(JSON.parse(savedStock)); } catch(e) { console.error('Error loading stock', e); } }
      
      const savedType = localStorage.getItem('pos_business_type');
      if (savedType) { this.selectedBusinessType.set(savedType); }

      const savedCatalog = localStorage.getItem('pos_product_catalog');
      if (savedCatalog) { try { this.productCatalog.set(JSON.parse(savedCatalog)); } catch(e) { console.error('Error loading product catalog', e); } }
      
      const savedStaff = localStorage.getItem('pos_staff_users');
      if (savedStaff) { 
          try { 
              const storedUsers: StaffUser[] = JSON.parse(savedStaff);
              // Reconcile staff users to preserve local changes while ensuring John Doe and mock users are never locked out
              const reconciledUsers = storedUsers.map(user => {
                  const mockUser = MOCK_STAFF_USERS.find(m => m.id === user.id);
                  if (user.id === 'USER-001') {
                    // John Doe (Manager) - Ensure guaranteed login access and Manager privileges
                    return {
                      ...mockUser,
                      ...user,
                      fullName: user.fullName || 'John Doe',
                      role: 'Manager' as const,
                      accessCode: user.accessCode || '654321',
                      defaultStoreId: user.defaultStoreId || 'store-01',
                      isActive: true,
                      initials: 'JD',
                      shiftStatus: 'Off Shift' as const,
                      permissions: { canVoid: true, canDiscount: true, canOpenDrawer: true, canEditSettings: true }
                    };
                  }
                  return mockUser ? { ...mockUser, ...user, shiftStatus: 'Off Shift' as const } : { ...user, shiftStatus: 'Off Shift' as const };
              });

              // Add any new users from MOCK_STAFF_USERS that aren't in local storage yet
              MOCK_STAFF_USERS.forEach(mockUser => {
                  if (!reconciledUsers.some(u => u.id === mockUser.id)) {
                      reconciledUsers.push({ ...mockUser, shiftStatus: 'Off Shift' });
                  }
              });

              this.staffUsers.set(reconciledUsers);
          } catch(e) { 
              console.error('Error loading staff, falling back to mock data.', e); 
              this.staffUsers.set(MOCK_STAFF_USERS);
          } 
      } else {
        this.staffUsers.set(MOCK_STAFF_USERS);
      }

      const savedLowStockEnabled = localStorage.getItem('pos_low_stock_alert_enabled');
      if (savedLowStockEnabled) { this.isLowStockAlertEnabled.set(JSON.parse(savedLowStockEnabled)); }

      const savedLowStockThreshold = localStorage.getItem('pos_low_stock_threshold');
      if (savedLowStockThreshold) { this.lowStockThreshold.set(JSON.parse(savedLowStockThreshold)); }
  }

  performBackgroundSync(isManual = false) {
    if (!this.isOnline()) {
        if (isManual) alert("Cannot sync while offline.");
        return;
    }
    if (this.isSyncing()) return;

    this.isSyncing.set(true);
    this.syncStatus.set('syncing');
    this.syncLog.update(log => [`[${new Date().toLocaleTimeString()}] Starting sync...`, ...log].slice(0, 10));

    setTimeout(() => {
        this.stockMap.update(currentStock => {
            const newStock = { ...currentStock };
            Object.keys(newStock).forEach(sku => {
                if (Math.random() > 0.8) {
                    const records = newStock[sku];
                    if (records && records.length > 0) {
                        const change = Math.floor(Math.random() * 3) - 1;
                        records[0].onHand = Math.max(0, records[0].onHand + change);
                    }
                }
            });
            return newStock;
        });
        this.syncLog.update(log => [`[${new Date().toLocaleTimeString()}] Fetched latest stock levels.`, ...log].slice(0, 10));
        
        this.lastSyncTime.set(new Date());
        this.isSyncing.set(false);
        this.syncStatus.set('success');
        this.syncLog.update(log => [`[${new Date().toLocaleTimeString()}] Sync complete!`, ...log].slice(0, 10));
        
        setTimeout(() => this.syncStatus.set('idle'), 3000);
    }, 1500);
  }

  processOfflineQueue() {
      const queue = this.offlineQueue();
      if (queue.length === 0) return;
      console.log(`Processing ${queue.length} offline transactions...`);
      setTimeout(() => {
          this.offlineQueue.set([]);
          console.log("Offline queue synced to server.");
      }, 1000);
  }
  
  selectUserForLogin(user: StaffUser) {
    this.selectedUserForLogin.set(user);
    this.accessCode.set('');
    this.loginError.set(false);
    this.loginErrorMessage.set(null);
    this.selectedStore.set(user.defaultStoreId);
  }
  
  deselectUserForLogin() {
    this.selectedUserForLogin.set(null);
    this.accessCode.set('');
    this.loginError.set(false);
    this.loginErrorMessage.set(null);
  }

  appendAccessCode(digit: string) { 
    this.loginError.set(false);
    this.loginErrorMessage.set(null);
    if (this.accessCode().length < 6) this.accessCode.update(c => c + digit); 
  }
  clearAccessCode() { 
    this.accessCode.set(''); 
    this.loginError.set(false);
    this.loginErrorMessage.set(null);
  }
  backspaceAccessCode() { 
    this.loginError.set(false);
    this.loginErrorMessage.set(null);
    this.accessCode.update(c => c.slice(0, -1)); 
  }
  toggleCashOpening() { this.enableCashOpening.update(v => !v); }

  fillUserPin(customPin?: string) {
    const user = this.selectedUserForLogin();
    const pin = customPin || user?.accessCode || (user?.id === 'USER-001' ? '654321' : '111111');
    this.accessCode.set(pin);
    this.loginError.set(false);
    this.loginErrorMessage.set(null);
  }

  quickLoginById(userId: string) {
    const user = this.staffUsers().find(u => u.id === userId) || this.staffUsers()[0];
    if (user) {
      this.quickLoginUser(user);
    }
  }

  quickLoginUser(user: StaffUser) {
    this.selectedUserForLogin.set(user);
    const pin = user.accessCode || (user.id === 'USER-001' ? '654321' : '111111');
    this.accessCode.set(pin);
    this.selectedStore.set(user.defaultStoreId || 'store-01');
    this.loginError.set(false);
    this.loginErrorMessage.set(null);
    this.performLogin();
  }

  performLogin() {
    let user = this.selectedUserForLogin();
    if (!user) {
      // If no user selected yet, default to John Doe (Manager)
      const defaultUser = this.staffUsers().find(u => u.id === 'USER-001') || this.staffUsers()[0];
      if (defaultUser) {
        this.selectUserForLogin(defaultUser);
        user = defaultUser;
      } else {
        return;
      }
    }

    const enteredCode = this.accessCode().trim();
    if (enteredCode.length !== 6) {
      this.loginError.set(true);
      this.loginErrorMessage.set('Please enter a 6-digit PIN code.');
      setTimeout(() => this.loginError.set(false), 500);
      return;
    }

    this.isAuthenticating.set(true);
    this.loginError.set(false);
    this.loginErrorMessage.set(null);
    this.loginSuccess.set(false);

    // Verify PIN: user's registered PIN, or for John Doe (Manager) allow 654321 / 123456 / 000000
    const isCodeValid = user.accessCode === enteredCode ||
      (user.id === 'USER-001' && (enteredCode === '654321' || enteredCode === '123456' || enteredCode === '000000')) ||
      (user.role === 'Manager' && enteredCode === '654321');

    // Fast authentication simulation (no blocking iframe window.confirm)
    setTimeout(() => {
      this.isAuthenticating.set(false);

      if (isCodeValid) {
        // --- SUCCESS ---
        this.loginSuccess.set(true);
        this.loginErrorMessage.set(null);

        // Smoothly transition into the terminal
        setTimeout(() => {
          this.currentUser.set(user);
          this.isLoggedIn.set(true);
          this.sessionStartTime.set(new Date());
          this.staffUsers.update(users => users.map(u => u.id === user.id ? { ...u, shiftStatus: 'On Shift' } : u));
          
          this.loginSuccess.set(false); 
          this.accessCode.set('');
          
          setTimeout(() => this.searchInput?.nativeElement?.focus(), 100);
        }, 300);

      } else {
        // --- ERROR ---
        this.accessCode.set('');
        this.loginError.set(true);
        this.loginErrorMessage.set(`Invalid PIN. Default Manager PIN for ${user.fullName} is ${user.accessCode || '654321'}.`);
        setTimeout(() => this.loginError.set(false), 500);
      }
    }, 400);
  }

  // --- FINOBOOKS AGENT API AUTH METHODS (https://agent.finobooks.com/api/Agent/Login) ---

  setLoginMode(mode: 'agent-api' | 'staff-pin') {
    this.loginMode.set(mode);
    this.loginError.set(false);
    this.loginErrorMessage.set(null);
  }

  toggleShowAgentPassword() {
    this.showAgentPassword.update(v => !v);
  }

  toggleAdvancedTelemetry() {
    this.showAdvancedTelemetry.update(v => !v);
  }

  fillTestAgentCredentials() {
    this.agentUsername.set('test011@litops.net');
    this.agentPassword.set('Test@12345');
    this.agentDeviceName.set('Windows');
    this.agentIpAddress.set('Unknown');
    this.agentCountryName.set('Unknown');
    this.agentCurrentLocation.set('Unknown');
    this.agentOsDetails.set(
      typeof navigator !== 'undefined' && navigator.userAgent
        ? navigator.userAgent
        : 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/154.0.0.0 Safari/537.36'
    );
    const nowIso = new Date().toISOString();
    this.agentLoginDateTime.set(nowIso);
    this.agentDeviceTime.set(nowIso);
    this.loginError.set(false);
    this.loginErrorMessage.set(null);
  }

  async performAgentApiLogin() {
    const username = this.agentUsername().trim();
    const password = this.agentPassword().trim();

    if (!username || !password) {
      this.loginError.set(true);
      this.loginErrorMessage.set('Please enter both Username and Password.');
      return;
    }

    this.isAuthenticating.set(true);
    this.isLoading.set(true); // ✅ Activate global loading flag until all APIs finish
    this.loginError.set(false);
    this.loginErrorMessage.set(null);
    this.loginSuccess.set(false);

    try {
      // ✅ Build device model using deviceService
      const device = await firstValueFrom(this.deviceService.buildDeviceModel());

      const nowIso = new Date().toISOString();
      this.agentLoginDateTime.set(nowIso);
      this.agentDeviceTime.set(nowIso);

      // Authenticate via authService login flow
      const user = await firstValueFrom(this.authService.login(username, password));

      const userValues = user?.userValues;
      const userName = userValues?.userName || username.split('@')[0] || 'Finobooks Agent';
      const updatedUserId = userValues?.userId || 'AGENT-001';
      const role = 'Agent Admin';
      const accessCode = '654321';

      const loggedInUser: StaffUser = {
        id: updatedUserId,
        fullName: `${userName} (Agent)`,
        role: role,
        accessCode: accessCode,
        defaultStoreId: this.selectedStore() || '',
        isActive: true,
        initials: userName ? userName.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'AG',
        shiftStatus: 'On Shift',
        permissions: {
          canVoid: true,
          canDiscount: true,
          canOpenDrawer: true,
          canEditSettings: true
        }
      };

      // Add or update in staff list
      this.staffUsers.update(users => {
        const idx = users.findIndex(u => u.id === loggedInUser.id);
        if (idx >= 0) {
          const list = [...users];
          list[idx] = loggedInUser;
          return list;
        }
        return [loggedInUser, ...users];
      });

      // Update store settings values with passkey and role from session/user
      if (user?.passkey) {
        this.settingPassKey.set(user.passkey);
      }
      if (user?.masterRoleId) {
        this.settingRoleId.set(user.masterRoleId);
      }
      if (userValues?.userId) {
        this.masterId.set(userValues.userId);
      }

      this.isAuthenticating.set(false);
      this.loginSuccess.set(true);
      this.currentUser.set(loggedInUser);
      this.isLoggedIn.set(true);
      this.sessionStartTime.set(new Date());
      this.loginSuccess.set(false);

      this.agentAuthSuccessNotice.set(`Agent authenticated: ${userName} (${userValues?.emailID || username}). Response stored.`);

      // Sync products first
      await this.syncProductsFromApi(true);

      // Fetch store details & subsequent lookups
      const result = await firstValueFrom(this.loadStoreGetByID());
      if (result) {
        this.storeSettings.set(result);
        this.loadStoreSettingsForCurrentStore();

        // Wrap parallel/sequential lookup API calls so we can await them all
        this.getLocationList(result?.branchID,result?.defaultWarehouse,'warehouse');
        this.getLocationList(result?.branchID,result?.defaultStockLocation,'stocklocation');
        this.getTaxTypeList(result?.defaultTaxCategory);
       this.getPartyAccountList(result?.defaultCustomer,result?.defaultPriceList);
       this.getAccountList(result?.defaultCashAccount,'defaultCashAccount');
       this.getAccountList(result?.cashSalesInvoice,'cashSalesInvoice');
       this.getAccountList(result?.defaultSalesAccount,'defaultSalesAccount');
       this.getBranchAddress(result?.branchID);

        console.log('Store data and all dependent lookups loaded successfully:', result);
      }

      this.apiToastMessage.set({
        text: `Logged in via Finobooks Agent API as ${userName}! Response securely stored.`,
        type: 'success'
      });
      setTimeout(() => this.apiToastMessage.set(null), 4000);

    } catch (err: any) {
      this.isAuthenticating.set(false);
      this.loginError.set(true);
      const errDetail = err?.message || 'Agent Login request failed';
      this.loginErrorMessage.set(`API Error: ${errDetail}`);
      setTimeout(() => this.loginError.set(false), 2000);
    } finally {
      this.isLoading.set(false); // ✅ Ensure loading stops whether success or error occurs
    }
  }

  enterTerminalWithStoredAgent() {
    const stored = this.agentAuth.storedResponse();
    if (!stored || !stored.result) return;

    const userValues = stored.result.userValues;
    const userName = userValues?.userName || 'Finobooks Agent';
    const agentUser: StaffUser = {
      id: userValues?.userId || 'ADM-268',
      fullName: `${userName} (Agent)`,
      role: 'Agent Admin',
      accessCode: '654321',
      defaultStoreId: this.selectedStore() || 'store-01',
      isActive: true,
      initials: (userName || 'AG').slice(0, 2).toUpperCase(),
      shiftStatus: 'On Shift',
      permissions: {
        canVoid: true,
        canDiscount: true,
        canOpenDrawer: true,
        canEditSettings: true
      }
    };

    this.currentUser.set(agentUser);
    this.isLoggedIn.set(true);
    this.sessionStartTime.set(new Date());
    this.apiToastMessage.set({
      text: `Terminal opened with active stored Finobooks Agent session (${userName})`,
      type: 'info'
    });
    setTimeout(() => this.apiToastMessage.set(null), 3000);
  }

  clearStoredAgentSession() {
    this.agentAuth.clearStoredAuth();
    this.agentAuthSuccessNotice.set(null);
    this.apiToastMessage.set({
      text: 'Stored Finobooks Agent API response and token cleared.',
      type: 'info'
    });
    setTimeout(() => this.apiToastMessage.set(null), 3000);
  }

  openAgentAuthInspector() {
    this.isAgentAuthInspectorOpen.set(true);
  }

  closeAgentAuthInspector() {
    this.isAgentAuthInspectorOpen.set(false);
  }

  copyAgentTokenToClipboard() {
    const token = this.agentAuth.activeToken();
    if (token && typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(token);
      this.apiToastMessage.set({
        text: 'Finobooks Agent JWT Token copied to clipboard!',
        type: 'success'
      });
      setTimeout(() => this.apiToastMessage.set(null), 3000);
    }
  }

  setInspectorTab(tab: 'auth' | 'profile') {
    this.activeInspectorTab.set(tab);
  }

  setProfileDrawerTab(tab: 'profile' | 'modules' | 'menus' | 'companies') {
    this.activeProfileDrawerTab.set(tab);
  }

  async refreshProfileDetails() {
    try {
      await this.agentAuth.fetchProfile();
      this.apiToastMessage.set({
        text: 'Profile details successfully fetched & stored from AdminSubscribe API!',
        type: 'success'
      });
      setTimeout(() => this.apiToastMessage.set(null), 3500);
    } catch (err: any) {
      const msg = err?.message || 'Failed to refresh profile details';
      this.apiToastMessage.set({
        text: `Profile Sync Error: ${msg}`,
        type: 'error'
      });
      setTimeout(() => this.apiToastMessage.set(null), 4000);
    }
  }

  copyProfileJsonToClipboard() {
    const prof = this.agentAuth.storedProfileResponse();
    if (prof && typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(JSON.stringify(prof, null, 2));
      this.apiToastMessage.set({
        text: 'AdminSubscribe Profile JSON copied to clipboard!',
        type: 'success'
      });
      setTimeout(() => this.apiToastMessage.set(null), 3000);
    }
  }

  copyProfileEndpointUrl() {
    const url = this.agentAuth.getProfileEndpointUrl();
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(url);
      this.apiToastMessage.set({
        text: 'Profile Endpoint URL copied to clipboard!',
        type: 'info'
      });
      setTimeout(() => this.apiToastMessage.set(null), 2500);
    }
  }

  logout() {
      const userId = this.currentUser()?.id;
      if (userId) {
        this.staffUsers.update(users => users.map(u => u.id === userId ? { ...u, shiftStatus: 'Off Shift' } : u));
      }
      this.isProfileDrawerOpen.set(false);
      this.isLoggedIn.set(false);
      this.accessCode.set('');
      this.loginErrorMessage.set(null);
      this.currentCustomer.set(null);
      this.currentUser.set(null);
      this.deselectUserForLogin();
  }

  lockTerminal() {
      this.isProfileDrawerOpen.set(false);
      this.isLoggedIn.set(false);
      this.accessCode.set('');
      this.deselectUserForLogin();
  }

  toggleProfileDrawer() { this.isProfileDrawerOpen.update(v => !v); }

  openSettings() {
      this.isProfileDrawerOpen.set(false);
      this.isSettingsModalOpen.set(true);
      this.settingsTab.set('store');
      this.loadStoreSettingsForCurrentStore();
  }

  closeSettings() { this.isSettingsModalOpen.set(false); }
  
  setSettingsTab(tab: 'store'|'business'|'product'|'barcode'|'stock'|'staff'|'system'|'customers'|'printer') {
    if (tab === 'customers') {
      this.selectedCustomerForDetail.set(null);
    }
    if (tab === 'store') {
      this.loadStoreSettingsForCurrentStore();
    }
    if (tab === 'printer') {
      this.posPrinter.autoDetectPrinters();
    }
    this.settingsTab.set(tab);
  }

  // --- POS Thermal Printer Management Methods ---

  onSelectPrinter(event: Event) {
    const select = event.target as HTMLSelectElement;
    const printerId = select.value;
    this.posPrinter.selectedPrinterId.set(printerId);
  }

  onSetDefaultPrinter(printerId: string) {
    const success = this.posPrinter.setDefaultPrinter(printerId);
    if (success) {
      const printer = this.posPrinter.detectedPrinters().find(p => p.id === printerId);
      this.showProductFeedback(`Default POS printer set to "${printer?.name || printerId}"`, 'success');
    } else {
      this.showProductFeedback(this.posPrinter.lastError() || 'Failed to set default printer', 'warning');
    }
  }

  async onRefreshPrinters() {
    this.showProductFeedback('Scanning for POS thermal printers and drivers...', 'info');
    const printers = await this.posPrinter.autoDetectPrinters();
    if (printers.length > 0) {
      this.showProductFeedback(`Detected ${printers.length} printer(s) & drivers successfully.`, 'success');
    } else {
      this.showProductFeedback('No thermal printers detected. Connect USB or start print agent.', 'warning');
    }
  }

  async onPairUsbPrinter() {
    this.showProductFeedback('Opening Chrome USB device picker... Please select your connected USB printer.', 'info');
    const paired = await this.posPrinter.autoScanUsbPrinters();
    if (paired) {
      this.showProductFeedback(`Paired & activated USB Thermal Printer: "${paired.name}"`, 'success');
    } else if (this.posPrinter.lastError()) {
      this.showProductFeedback(this.posPrinter.lastError()!, 'warning');
    }
  }

  onInstallUsbDriver(driverId: string) {
    const installed = this.posPrinter.installUsbDriverProfile(driverId);
    if (installed) {
      this.showProductFeedback(`Installed & activated USB Driver: "${installed.name}"`, 'success');
    }
  }

  onBroadcastPluginDiscovery() {
    this.showProductFeedback('Broadcasting discovery handshake to Chrome Extensions & Plugins...', 'info');
    this.posPrinter.broadcastPluginDiscovery();
    setTimeout(() => {
      if (this.posPrinter.chromePluginStatus() === 'connected') {
        this.showProductFeedback(`Chrome Plugin Connected: ${this.posPrinter.detectedPluginInfo()?.name || 'Active'}`, 'success');
      } else {
        this.showProductFeedback('No Chrome POS Extension responded. You can use direct WebUSB or OS USB driver.', 'info');
      }
    }, 1500);
  }

  async onScanDeviceManager() {
    this.showProductFeedback('Scanning Windows Device Manager for installed POS thermal drivers...', 'info');
    const drivers = await this.posPrinter.scanSystemDeviceManagerPrinters();
    const posiflex = drivers.find(d => d.isPosiflex);
    if (posiflex) {
      this.showProductFeedback(`Detected Posiflex Driver in Device Manager: "${posiflex.driverName}"`, 'success');
    } else {
      this.showProductFeedback(`Device Manager scan found ${drivers.length} installed POS printer driver queues.`, 'info');
    }
  }

  onBindDeviceManagerDriver(driver: DeviceManagerDriver) {
    const bound = this.posPrinter.bindDeviceManagerDriver(driver);
    this.showProductFeedback(`Activated Device Manager Driver: "${bound.name}" (Default Printer)`, 'success');
  }

  onAddCustomDriver(input: HTMLInputElement) {
    const name = input.value.trim();
    if (!name) return;
    const added = this.posPrinter.addCustomDeviceManagerDriver(name);
    input.value = '';
    this.showProductFeedback(`Bound custom Device Manager driver "${added.name}" as Default Printer`, 'success');
  }

  onQuickBindPosiflex() {
    const posiflexDriver = this.posPrinter.standardUsbDrivers.find(d => d.id === 'driver:posiflex_aura_pp8800');
    if (posiflexDriver) {
      const bound = this.posPrinter.installUsbDriverProfile(posiflexDriver.id);
      if (bound) {
        this.showProductFeedback(`Posiflex Aura PP-8800 set as Default POS Thermal Printer!`, 'success');
      }
    }
  }

  async onPrintTestTicket() {
    const printer = this.posPrinter.activePrinter();
    if (!printer) {
      this.showProductFeedback('No POS thermal printer available to print test ticket.', 'warning');
      return;
    }

    this.showProductFeedback(`Sending test ticket to "${printer.name}"...`, 'info');
    const result = await this.posPrinter.printTestTicket();
    if (result.success) {
      this.showProductFeedback(`Test ticket printed successfully on ${result.printerName}`, 'success');
    } else {
      this.showProductFeedback(`Test print error: ${result.message}`, 'warning');
    }
  }

  onChangePaperWidth(width: '80mm' | '58mm') {
    this.posPrinter.saveConfiguration({ paperWidth: width });
    this.showProductFeedback(`Thermal receipt format updated to ${width}.`, 'info');
  }

  onToggleAutoCut(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.posPrinter.saveConfiguration({ autoCut: checked });
  }

  onToggleOpenDrawer(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.posPrinter.saveConfiguration({ openCashDrawer: checked });
  }

  onToggleAutoPrint(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.posPrinter.saveConfiguration({ autoPrintReceipt: checked });
  }

  onChangePrintDensity(event: Event) {
    const select = event.target as HTMLSelectElement;
    const density = select.value as 'normal' | 'dark' | 'light';
    this.posPrinter.saveConfiguration({ printDensity: density });
  }

  onUpdateAgentUrl(event: Event) {
    const input = event.target as HTMLInputElement;
    const url = input.value.trim() || 'http://127.0.0.1:8080';
    this.posPrinter.saveConfiguration({ localAgentUrl: url });
    this.showProductFeedback(`Local print agent URL updated to ${url}`, 'info');
  }

  async reprintLastReceipt() {
    this.showProductFeedback('Dispatching reprint to default thermal printer...', 'info');
    const result = await this.posPrinter.reprintLastReceipt();
    if (result.success) {
      this.showProductFeedback(`Reprint sent to "${result.printerName}"`, 'success');
    } else {
      this.showProductFeedback(`Reprint notice: ${result.message}`, 'warning');
    }
  }

  // --- Store Settings Implementation ---
  // loadStoreSettingsForCurrentStore() {
  //   const setting = this.storeSettingsService.findAnySettingForStore(this.selectedStore());
  //   if (setting) {
  //     this.populateFormWithSetting(setting);
  //   } else {
  //     this.initNewStoreSettingForm();
  //     this.settingStore.set(this.selectedStore());
  //     const storeObj = this.stores.find(s => s.id === this.selectedStore());
  //     if (storeObj) this.settingStoreName.set(storeObj.name);
  //   }
  // }
  loadStoreSettingsForCurrentStore() {
    const setting = this.storeSettings();
    if (setting) {
      this.populateFormWithSetting(setting);
    } else {
      this.initNewStoreSettingForm();
      this.settingStore.set(this.selectedStore());
      const storeObj = this.stores.find(s => s.id === this.selectedStore());
      if (storeObj) this.settingStoreName.set(storeObj.name);
    }
  }
  selectStoreSettingToEdit(setting: StoreSetting) {
    this.populateFormWithSetting(setting);
  }

  populateFormWithSetting(setting: StoreSetting) {
    this.storeSettingId.set(setting.id);
    this.masterId.set(setting.masterId);
    this.subscriptionId.set(setting.subscriptionId);
    this.settingCompany.set(setting.company);
    this.settingBranch.set(setting.branchID);
    this.settingBranchName.set(setting.branchName);
    this.settingBranchState.set(setting.branchState || 'HYD');
    this.settingLocation.set(setting.locationID);
    this.settingLocationName.set(setting.locationName);
    this.settingStore.set(setting.store);
    this.settingStoreName.set(setting.storeName);
    // this.settingDefaultCashAccount.set(setting.defaultCashAccount);
    // this.settingCashSalesInvoice.set(setting.cashSalesInvoice);
    // this.settingDefaultSalesAccount.set(setting.defaultSalesAccount);
  //  this.settingDefaultCustomer.set(setting.defaultCustomer);
   //this.settingDefaultWarehouse.set(setting.defaultWarehouse);
    //this.settingDefaultStockLocation.set(setting.defaultStockLocation);
    // this.settingDefaultPriceList.set(setting.defaultPriceList);
    // this.settingDefaultTaxCategory.set(setting.defaultTaxCategory);
    this.settingDefaultPaymentMode.set(setting.defaultPaymentMode);
    this.settingDefaultCurrency.set(setting.defaultCurrency);
    // Header Binding Parameters
    // this.settingSalesAccountName.set(setting.salesAccountName || 'Account');
    // this.settingStockControlAccountID.set(setting.stockControlAccountID || 'Control');
    // this.settingStockControlAccountName.set(setting.stockControlAccountName || 'name');
    // this.settingSalesCashAccountName.set(setting.salesCashAccountName || 'CashNAmeABC');
    this.settingCounterOperatorID.set(setting.counterOperatorID || 'operator-01');
    this.settingCounterOperatorName.set(setting.counterOperatorName || 'Operator-ABC');
    this.settingIsCreditSales.set(setting.isCreditSales ?? true);
    this.settingInvoiceRequest.set(setting.isInvoiceRequest ?? true);
    this.settingIsEnableStorageBin.set(setting.isEnableStorageBin ?? false);
    this.settingIsEnableTransportation.set(setting.isEnableTransportation ?? true);
    this.settingIsEnableOtherInfo.set(setting.isEnableOtherInfo ?? true);
    this.settingIsDiscountOnSummary.set(setting.isDiscountOnSummary ?? false);
    // this.settingTaxTypeID.set(setting.defaultTaxTypeID || 'gst');
    this.settingTaxTypeCode.set(setting.defaultTaxTypeCode || 'gst');
    this.settingTaxRateID.set(setting.defaultTaxRateID || 'taa-11');
    this.settingTaxRateValue.set(setting.defaultTaxRateValue ?? 87.9);
    this.settingTaxRateCode.set(setting.defaultTaxRateCode || 'ta-678');
    this.settingPaymentTerms.set(setting.defaultPaymentTerms || 'assa');
    this.settingShippingAddressId.set(setting.shippingAddressId || 'shipadd-12');
    this.settingShippingLabelName.set(setting.shippingLabelName || 'shiplable-12');
    this.settingShippingReceiverName.set(setting.shippingReceiverName || 'shipRe-12');
    this.settingBillingAddressId.set(setting.billingAddressId || 'billad-12');
    this.settingBillingLabelName.set(setting.billingLabelName || 'billlab-12');
    this.settingBillingReceiverName.set(setting.billingReceiverName || 'billre-12');
    this.settingIsActive.set(setting.isActive);
    this.settingParameters.set(setting.parameters ? [...setting.parameters] : []);
    this.storeSettingFeedback.set(null);
  }

  initNewStoreSettingForm() {
    this.storeSettingId.set(null);
    this.masterId.set('MST-' + Math.floor(10000 + Math.random() * 90000));
    this.subscriptionId.set('SUB-OCTO-2026');
    this.settingCompany.set('COMP-01');
    const branches = this.storeSettingsService.getBranches('COMP-01');
    const branch = branches[0] || { id: 'BR-TX01', name: 'Texas Central Hub' };
    this.settingBranch.set(branch.id);
    this.settingBranchName.set(branch.name);
    this.settingBranchState.set('HYD');
    const locations = this.storeSettingsService.getLocations(branch.id);
    const loc = locations[0] || { id: 'LOC-HOU', name: 'Houston Downtown Logistics Center' };
    this.settingLocation.set(loc.id);
    this.settingLocationName.set(loc.name);
    const stores = this.storeSettingsService.getStores(loc.id);
    const st = stores[0] || { id: 'store-01', name: 'Store 001 - Main St (HQ)' };
    this.settingStore.set(st.id);
    this.settingStoreName.set(st.name);
    this.settingDefaultCashAccount.set('KOOP');
    this.settingCashSalesInvoice.set('INV-CS-AUTO');
    this.settingDefaultSalesAccount.set('');
    this.settingDefaultCustomer.set('006948b0-aef1-4447-854d-69892717f03f');
    this.settingDefaultWarehouse.set('WH-STORE01');
    this.settingDefaultStockLocation.set('LOC-SHELF-A');
    this.settingDefaultPriceList.set('PL-STANDARD');
    this.settingDefaultTaxCategory.set('TAX-STD');
    this.settingDefaultPaymentMode.set('Cash');
    this.settingDefaultCurrency.set('USD');
    // Header Defaults
    this.settingPassKey.set('PK-992014-X');
    this.settingRoleId.set('ROLE-MGR-01');
    this.settingSalesAccountName.set('Account');
    this.settingStockControlAccountID.set('');
    this.settingStockControlAccountName.set('');
    this.settingSalesCashAccountName.set('CashNAmeABC');
    this.settingCounterOperatorID.set('operator-01');
    this.settingCounterOperatorName.set('Operator-ABC');
    this.settingIsCreditSales.set(true);
    this.settingInvoiceRequest.set(true);
    this.settingIsEnableStorageBin.set(false);
    this.settingIsEnableTransportation.set(true);
    this.settingIsEnableOtherInfo.set(true);
    this.settingIsDiscountOnSummary.set(false);
    this.settingTaxTypeID.set('gst');
    this.settingTaxTypeCode.set('gst');
    this.settingTaxRateID.set('taa-11');
    this.settingTaxRateValue.set(87.9);
    this.settingTaxRateCode.set('ta-678');
    this.settingPaymentTerms.set('assa');
    this.settingShippingAddressId.set('shipadd-12');
    this.settingShippingLabelName.set('shiplable-12');
    this.settingShippingReceiverName.set('shipRe-12');
    this.settingBillingAddressId.set('billad-12');
    this.settingBillingLabelName.set('billlab-12');
    this.settingBillingReceiverName.set('billre-12');
    this.settingIsActive.set(true);
    this.settingParameters.set([
      { key: 'allow_negative_inventory', label: 'Allow Negative Inventory', value: 'false' },
      { key: 'auto_print_receipt', label: 'Auto Print Thermal Receipt', value: 'true' },
      { key: 'pos_counter_number', label: 'Terminal Counter ID', value: 'POS-TERM-01' }
    ]);
    this.storeSettingFeedback.set(null);
  }

  onCompanyChange(companyId: string) {
    this.settingCompany.set(companyId);
    const branches = this.storeSettingsService.getBranches(companyId);
    if (branches.length > 0) {
      this.onBranchChange(branches[0].id);
    }
  }

  onBranchChange(branchId: string) {
    this.settingBranch.set(branchId);
    const branchObj = this.storeSettingsService.getBranches().find(b => b.id === branchId);
    if (branchObj) this.settingBranchName.set(branchObj.name);
    
    const locations = this.storeSettingsService.getLocations(branchId);
    if (locations.length > 0) {
      this.onLocationChange(locations[0].id);
    }
  }

  onLocationChange(locationId: string) {
    this.settingLocation.set(locationId);
    const locObj = this.storeSettingsService.getLocations().find(l => l.id === locationId);
    if (locObj) this.settingLocationName.set(locObj.name);

    const stores = this.storeSettingsService.getStores(locationId);
    if (stores.length > 0) {
      this.onStoreChange(stores[0].id);
    }
  }

  onStoreChange(storeId: string) {
    this.settingStore.set(storeId);
    const storeObj = this.storeSettingsService.getStores().find(s => s.id === storeId);
    if (storeObj) this.settingStoreName.set(storeObj.name);

    // If existing setting exists for this store, load it so user can update
    const existing = this.storeSettingsService.findAnySettingForStore(storeId);
    if (existing && this.storeSettingId() !== existing.id) {
      this.populateFormWithSetting(existing);
    }
  }

  toggleSettingActive() {
    this.settingIsActive.update(v => !v);
  }

  addDynamicParam() {
    const key = this.newParamKey().trim().toLowerCase().replace(/\s+/g, '_');
    const label = this.newParamLabel().trim() || key;
    const value = this.newParamValue().trim();
    if (!key) {
      alert('Please enter a parameter key name.');
      return;
    }
    this.settingParameters.update(params => [...params, { key, label, value }]);
    this.newParamKey.set('');
    this.newParamLabel.set('');
    this.newParamValue.set('');
  }

  removeDynamicParam(index: number) {
    this.settingParameters.update(params => params.filter((_, i) => i !== index));
  }

  async saveOrUpdateStoreSettings() {
    // 1. Validate mandatory fields
    const mId = this.masterId().trim();
    const sId = this.subscriptionId().trim();
    const comp = this.settingCompany();
    const br = this.settingBranch();
    const brName = this.settingBranchName().trim();
    const loc = this.settingLocation();
    const locName = this.settingLocationName().trim();
    const st = this.settingStore();
    const stName = this.settingStoreName().trim();
    const cashAcc = this.settingDefaultCashAccount();
    const csInv = this.settingCashSalesInvoice();
    const salesAcc = this.settingDefaultSalesAccount();
    const cust = this.settingDefaultCustomer();
    const wh = this.settingDefaultWarehouse();
    const stockLoc = this.settingDefaultStockLocation();
    const pl = this.settingDefaultPriceList();
    const taxCat = this.settingDefaultTaxCategory();
    const payMode = this.settingDefaultPaymentMode();
    const curr = this.settingDefaultCurrency();

    if (!mId || !sId || !comp || !br || !brName || !loc || !locName || !st || !stName ||
        !cashAcc || !csInv || !salesAcc || !cust || !wh || !stockLoc || !pl || !taxCat || !payMode || !curr) {
      this.storeSettingFeedback.set({
        type: 'error',
        message: 'All fields marked with an asterisk (*) are mandatory. Please fill in all required fields.'
      });
      return;
    }

    this.isSavingStoreSetting.set(true);
    this.storeSettingFeedback.set(null);

    const payload: Omit<StoreSetting, 'id' | 'createdAt' | 'updatedAt'> = {
      masterId: mId,
      subscriptionId: sId,
      company: comp,
      companyName: this.companiesList.find(c => c.id === comp)?.name || comp,
      branchID: br,
      branchName: brName,
      branchState: this.settingBranchState(),
      locationID: loc,
      locationName: locName,
      store: st,
      storeName: stName,
      defaultCashAccount: cashAcc,
      cashSalesInvoice: csInv,
      defaultSalesAccount: salesAcc,
      defaultCustomer: cust,
      defaultWarehouse: wh,
      defaultStockLocation: stockLoc,
      defaultPriceList: pl,
      defaultTaxCategory: taxCat,
      defaultPaymentMode: payMode,
      defaultCurrency: curr,
      // Header Binding Parameters
      passKey: this.settingPassKey(),
      roleId: this.settingRoleId(),
      salesAccountName: this.settingSalesAccountName(),
      stockControlAccountID: this.settingStockControlAccountID(),
      stockControlAccountName: this.settingStockControlAccountName(),
      salesCashAccountName: this.settingSalesCashAccountName(),
      counterOperatorID: this.settingCounterOperatorID(),
      counterOperatorName: this.settingCounterOperatorName(),
      isCreditSales: this.settingIsCreditSales(),
      isInvoiceRequest: this.settingInvoiceRequest(),
      isEnableStorageBin: this.settingIsEnableStorageBin(),
      isEnableTransportation: this.settingIsEnableTransportation(),
      isEnableOtherInfo: this.settingIsEnableOtherInfo(),
      isDiscountOnSummary: this.settingIsDiscountOnSummary(),
      defaultTaxTypeID: this.settingTaxTypeID(),
      defaultTaxTypeCode: this.settingTaxTypeCode(),
      defaultTaxRateID: this.settingTaxRateID(),
      defaultTaxRateValue: this.settingTaxRateValue(),
      defaultTaxRateCode: this.settingTaxRateCode(),
      defaultPaymentTerms: this.settingPaymentTerms(),
      shippingAddressId: this.settingShippingAddressId(),
      shippingLabelName: this.settingShippingLabelName(),
      shippingReceiverName: this.settingShippingReceiverName(),
      billingAddressId: this.settingBillingAddressId(),
      billingLabelName: this.settingBillingLabelName(),
      billingReceiverName: this.settingBillingReceiverName(),
      isActive: this.settingIsActive(),
      parameters: this.settingParameters()
    };

    try {
      if (this.storeSettingId()) {
        // PUT /api/store/settings/{SettingID}
        const res = await this.storeSettingsService.updateStoreSetting(this.storeSettingId()!, payload);
        if (res.success && res.data) {
          this.storeSettingFeedback.set({
            type: 'success',
            message: `Store Settings updated successfully! (ID: ${res.data.id})`
          });
          this.populateFormWithSetting(res.data);
        } else {
          this.storeSettingFeedback.set({
            type: 'error',
            message: res.message || 'Failed to update Store Settings.',
            code: res.error
          });
        }
      } else {
        // POST /api/store/settings
        const res = await this.storeSettingsService.createStoreSetting(payload);
        if (res.success && res.data) {
          this.storeSettingFeedback.set({
            type: 'success',
            message: `Store Settings saved successfully! (Generated ID: ${res.data.id})`
          });
          this.populateFormWithSetting(res.data);
        } else {
          this.storeSettingFeedback.set({
            type: 'error',
            message: res.message || 'Failed to save Store Settings.',
            code: res.error
          });
        }
      }
    } catch (err: any) {
      this.storeSettingFeedback.set({
        type: 'error',
        message: err?.message || 'An unexpected error occurred while communicating with the Store Settings API.'
      });
    } finally {
      this.isSavingStoreSetting.set(false);
    }
  }

  handlePayClick() {
    // Check whether Store Settings are configured for active store
    const storeSetting = this.storeSettingsService.getSettingForStore(this.selectedStore());
    if (!storeSetting) {
      this.isStoreConfiguredAlertOpen.set(true);
      return;
    }

    // Apply defaults from Store Settings
    this.applyStoreSettingsDefaults(storeSetting);
    this.setTab('payments');
  }

  applyStoreSettingsDefaults(storeSetting: StoreSetting) {
    if (!this.currentCustomer()) {
      const foundCust = this.customers().find(c => c.id === storeSetting.defaultCustomer);
      if (foundCust) {
        this.currentCustomer.set(foundCust);
      }
    }
  }

  closeStoreConfiguredAlert() {
    this.isStoreConfiguredAlertOpen.set(false);
  }

  navigateToStoreSettings() {
    this.isStoreConfiguredAlertOpen.set(false);
    this.isProfileDrawerOpen.set(false);
    this.isSettingsModalOpen.set(true);
    this.setSettingsTab('store');
    this.loadStoreSettingsForCurrentStore();
  }
  
  selectBusinessType(id: string) { this.selectedBusinessType.set(id); }

  generateStoreToken(storeId: string) {
    const token = Math.random().toString(36).substring(2, 8).toUpperCase();
    const expires = Date.now() + 60000; // Token valid for 60 seconds
    this.storeTokens.update(tokens => ({
      ...tokens,
      [storeId]: { token, expires }
    }));
  }

  registerDevice() {
    const token = this.deviceRegistrationToken().trim();
    if (!token) {
      alert("Please enter a registration token.");
      return;
    }

    // Simulation of finding the store linked to the token
    const allTokens = this.storeTokens();
    const storeId = Object.keys(allTokens).find(id => allTokens[id].token === token);
    const store = this.stores.find(s => s.id === storeId);

    if (store) {
      alert(`Success! This device has been registered to:\n\n${store.name}`);
      console.log(`Simulating POST to server: { deviceId: 'this-device-uuid', storeId: '${storeId}', token: '${token}' }`);
      this.deviceRegistrationToken.set('');
    } else {
      alert("Invalid or expired token. Please try again.");
    }
  }

  // --- Staff Management Methods ---
  openStaffModal(user: StaffUser | null) {
    if (user) {
      this.editingStaffUser.set(user);
      this.staffUserForm.set({ ...user });
    } else {
      const defaultRole: StaffUser['role'] = 'Cashier';
      this.editingStaffUser.set(null);
      this.staffUserForm.set({
        id: '',
        fullName: '',
        role: defaultRole,
        accessCode: (Math.floor(Math.random() * 900000) + 100000).toString(),
        defaultStoreId: 'store-01',
        isActive: true,
        permissions: this.defaultPermissionsByRole[defaultRole]
      });
    }
    this.isStaffModalOpen.set(true);
  }

  closeStaffModal() {
    this.isStaffModalOpen.set(false);
  }

  onStaffUserFormInput(field: keyof Omit<StaffUser, 'isActive' | 'initials' | 'id' | 'shiftStatus' | 'permissions'>, event: Event) {
      const input = event.target as HTMLInputElement | HTMLSelectElement;
      this.staffUserForm.update(f => ({ ...f, [field]: input.value }));
  }
  
  onRoleChangeInForm(event: Event) {
    const role = (event.target as HTMLSelectElement).value as StaffUser['role'];
    this.staffUserForm.update(f => ({
        ...f,
        role,
        permissions: this.defaultPermissionsByRole[role]
    }));
  }

  togglePermission(permission: keyof StaffUser['permissions']) {
    this.staffUserForm.update(f => {
        const newPermissions = { ...f.permissions };
        newPermissions[permission] = !newPermissions[permission];
        return { ...f, permissions: newPermissions as StaffUser['permissions'] };
    });
  }


  toggleStaffUserActive() {
      this.staffUserForm.update(f => ({ ...f, isActive: !f.isActive }));
  }

  saveStaffUser() {
      const formData = this.staffUserForm();
      if (!formData.fullName?.trim() || !formData.accessCode?.trim()) {
          alert("Full Name and Access Code are required.");
          return;
      }

      if (this.editingStaffUser()) {
          // Update
          this.staffUsers.update(users => 
              users.map(u => {
                if (u.id === formData.id) {
                  return {
                     ...u,
                     ...formData,
                     initials: formData.fullName!.split(' ').map(n=>n[0]).join('').substring(0,2).toUpperCase()
                  } as StaffUser;
                }
                return u;
              })
          );
      } else {
          // Add
          const newUser: StaffUser = {
              id: `USER-${Date.now().toString().slice(-6)}`,
              fullName: formData.fullName,
              role: formData.role || 'Cashier',
              accessCode: formData.accessCode,
              defaultStoreId: formData.defaultStoreId || 'store-01',
              isActive: formData.isActive ?? true,
              // FIX: Added non-null assertion as `fullName` is guaranteed to be a string here due to the check above.
              initials: formData.fullName!.split(' ').map(n => n[0]).join('').substring(0,2).toUpperCase(),
              shiftStatus: 'Off Shift',
              permissions: formData.permissions || this.defaultPermissionsByRole['Trainee']
          };
          this.staffUsers.update(users => [...users, newUser]);
      }
      this.closeStaffModal();
  }

  deleteStaffUser(userId: string) {
      if (this.currentUser()?.id === userId) {
          alert("You cannot delete your own account while logged in.");
          return;
      }
      if (confirm("Are you sure you want to delete this staff user?")) {
          this.staffUsers.update(users => users.filter(u => u.id !== userId));
      }
  }

  getStoreName(storeId: string): string {
    const store = this.stores.find(s => s.id === storeId);
    return store?.name || 'N/A';
  }

  // Barcode Printing Methods
  selectItemForBarcode(index: number) {
    this.barcodeSelectedItemIndex.set(index);
  }

  async printBarcodeLabel() {
    const item = this.barcodeSelectedItem();
    if (item) {
      const result = await this.posPrinter.printBarcodeLabel({
        sku: item.pr_ProductSkuID,
        name: item.pr_ProductName,
        barcode: item.pr_ProductCode,
        price: item.pr_DefaultSalesMRPPrice
      });
      if (result.success) {
        this.showProductFeedback(`Barcode label printed via ${result.printerName}`, 'success');
      } else {
        this.showProductFeedback(`Label print warning: ${result.message}`, 'warning');
      }
    } else {
      this.showProductFeedback('Please select an item from the cart to print its label.', 'warning');
    }
  }

  openZReport() {
    this.isProfileDrawerOpen.set(false);
    this.isZReportOpen.set(true);
    this.zReportTab.set('summary');
  }

  closeZReport() { this.isZReportOpen.set(false); }
  setZReportTab(tab: 'summary' | 'transactions') { this.zReportTab.set(tab); }
  selectTransaction(trx: Transaction) { this.selectedTransaction.set(trx); }

  async printZReport() {
    const reportData = {
      sessionSales: this.totalSessionSales(),
      transactionsCount: this.sessionTransactions().length,
      voidedAmount: this.totalVoidedAmount(),
      voidedCount: this.voidedBills().length,
      terminal: 'POS-01',
      cashier: this.currentUser()?.fullName || 'Cashier',
      timestamp: new Date().toISOString()
    };

    this.showProductFeedback('Printing Z-Report to thermal printer...', 'info');
    const result = await this.posPrinter.printZReport(reportData);
    if (result.success) {
      this.showProductFeedback(`Z-Report printed to ${result.printerName}`, 'success');
      setTimeout(() => { this.closeZReport(); }, 800);
    } else {
      this.showProductFeedback(`Z-Report warning: ${result.message}`, 'warning');
    }
  }

  /**
   * Constructs the Counter Sales Invoice payload according to /api/LI210911101004_25EFCounterSalesInvoice/Insert specifications
   xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx */
  
 // 1. Convert buildCounterSalesInvoicePayload to async / return Promise via firstValueFrom
 async buildCounterSalesInvoicePayload(submitType: 'SAVE' | 'HOLD' | 'DRAFT', billRef?: string): Promise<CounterSalesInvoicePayload> {
  const auth = await firstValueFrom(this.getAuthContext());

  const payload = {
    subscriptionID: auth?.subscriptionId?.toString(),
    passKey: auth?.passKey,
    masterID: auth?.masterId,
    companyID: auth?.companyId?.toString(),
    roleID: auth?.masterRoleId,
  }; 

  const storeSetting = this.storeSettingsService.getSettingForStore(this.selectedStore()) || this.storeSettingsService.storeSettings()[0];
  const customer = this.currentCustomer();
  const operator = this.currentUser();
  const nowIso = new Date().toISOString();
  const trnsDate = nowIso.split('T')[0];

  // Build csiProducts from cart using calculateLineTotals logic equivalents
  const csiProducts: any[] = this.cart().map((item, idx) => {

    const inputQty = Number(item.qty || item.csiProductQty) || 0;
    const costPrice = Number(item.csiProductCostPrice) || 0;
    const mrpPrice = Number(item.csiProductMrpPrice) || 0;

    const discType = item.csiDiscountType == 'percent' ? 'PERCENT' : 'VALUE';
    const discVal = Number(item.csiProductDiscount || item.discount || 0);
    const isInclTax = true;
    const prqNonTaxable = false;

    const isAltUomActive = item.csiProductIsAltUomEnabled === true;
    const selectedUomType = item.csiProductAltUserUOMID;
    const factor = Number(item.csiProductQtyInSetBase) || 0;
    
    let totalCalculatedQty = inputQty;
    if (isAltUomActive && selectedUomType === 'Alt') {
      totalCalculatedQty = inputQty * factor;
    }

    const rawTotal = totalCalculatedQty * costPrice;

    let discountAmount = 0;
    if (discType === 'VALUE') {
      discountAmount = discVal;
    } else if (discType === 'PERCENT' || discType === 'PERCENTAGE' || discType === '%') {
      discountAmount = (rawTotal * discVal) / 100;
    } else {
      discountAmount = discVal > 0 && discType !== 'VALUE' ? (rawTotal * discVal) / 100 : discVal;
    }
    discountAmount = Math.min(discountAmount, rawTotal);

    let grossValue = rawTotal - discountAmount;
    let totalTaxRate = Number(item.csiProductTaxRate || 0);
    const isInterState =(this.settingPartyState()).toLowerCase() !== (this.settingBranchState()).toLowerCase();
  let taxType= item?.csiProductTaxType || 'GST'
    if((this.settingPartyState()).toLowerCase() != (this.settingBranchState()).toLowerCase())
   {
      taxType = 'IGST'
   }
   
    if (!prqNonTaxable) {
      if (taxType === 'GST' || taxType === 'IGST') {
        const currentIgst = Number(item?.csiigstRate) || 0;
        const currentCgst = Number(item?.csicgstRate) || 0;
        const currentSgst = Number(item?.csisgstRate) || 0;
  
        totalTaxRate = (isInterState || taxType === 'IGST') 
          ? currentIgst 
          : (currentCgst + currentSgst);
      } else if (taxType === 'VAT') {
        const vatRate = Number(item?.csiVatRate);
        totalTaxRate = !isNaN(vatRate) && item?.csiVatRate !== null 
          ? vatRate 
          : (Number(item?.csiProductTaxRate) || 0);
      }
    }
   
    if (!prqNonTaxable && isInclTax && totalTaxRate > 0) {
      grossValue = (grossValue * 100) / (100 + totalTaxRate);
    }
    let taxableBase = grossValue;

    let totalTaxValue = 0;
    let finalTotalWithTax = taxableBase;
    
    if (!prqNonTaxable && totalTaxRate > 0) {
      totalTaxValue = taxableBase * (totalTaxRate / 100);
      finalTotalWithTax = taxableBase + totalTaxValue;
    } else {
      totalTaxValue = 0;
    }

    const finalProductTotalValue = Number(finalTotalWithTax);
    let igstRate = 0, cgstRate = 0, sgstRate = 0, vatRate = 0;
    let igstAmt = 0, cgstAmt = 0, sgstAmt = 0, vatAmt = 0;
    
    if (!prqNonTaxable && totalTaxRate > 0) {
      if (taxType === 'GST' || taxType === 'IGST') {
        if (isInterState || taxType === 'IGST') {
          igstRate = totalTaxRate;
          igstAmt = totalTaxValue;
        } else {
          cgstRate = totalTaxRate / 2;
          sgstRate = totalTaxRate / 2;

          cgstAmt = Number((totalTaxValue / 2).toFixed(2));
          sgstAmt = totalTaxValue - cgstAmt;
        }
      } else if (taxType === 'VAT') {
        vatRate = totalTaxRate;
        vatAmt = totalTaxValue;
      }
    }

    return {
      csipid: item.csipid || `0b3d8b4f-7a29-4961-9b11-${String(idx).padStart(12, '0')}`,
      csiProductID: item.csiProductID || item.pr_ProductID || `0b3d8b4f-7a29-4961-9b11-${String(idx).padStart(12, '0')}`,
      csiProductCode: item.csiProductCode || item.sku || item.barcode || 'PARA-30339',
      csiIsBatch: item.csiIsBatch ?? false,
      csiProductName: item.csiProductName || item.pr_ProductName || 'Water Pump Eicher',
      csiProductDesc: item.csiProductDesc || item.productDesc || 'Used for pain relief and fever.',
      csiProductCategoryID: item.csiProductCategoryID || item.categoryId || 'category',
      csiProductCategoryName: item.csiProductCategoryName || item.categoryName || 'category-A',
      csiProductBrandID: item.csiProductBrandID || item.brandId || 'test brand',
      csiProductBrandName: item.csiProductBrandName || item.brandName || 'test brand',
      csiProductUomID: item.csiProductUomID || item.uomId || 'ab7ef292-0bb5-4330-9c35-a306246f413f',
      csiProductUomName: item.csiProductUomName || item.uomName || item.unit || 'test uom',
      csiProductQty: inputQty.toString(),
      csiProductAltUserUOMID: item?.csiProductAltUserUOMID || null,
      csiProductAltUserUOMName: item?.csiProductAltUserUOMName || null,
      csiProductIsAltUomEnabled: isAltUomActive,
      csiProductQtyInSetBase: factor,
      csiIsInclTax: isInclTax,
      csiDiscountType: (discType === 'PERCENT' ? 'PERCENTAGE' : 'VALUE') as 'VALUE' | 'PERCENTAGE',
      csiProductDiscount: discVal.toString(),
      csiProductGrossValue: rawTotal.toFixed(2),
      csiProductGrossDiscountValue: (rawTotal - discountAmount).toFixed(2),
      csiProductTotalValue: finalProductTotalValue.toString(),
      csiProductCostPrice: costPrice.toString(),
      csiProductMrpPrice: mrpPrice.toString(),
      csiProductDiscountValue: discountAmount.toFixed(2),
      csiProductTaxValue: totalTaxValue.toFixed(2),
      csiProductRemarks: item.csiProductRemarks || item.remarks || item.note || '',
      csiid: item.csiid || `csi-${Date.now()}-${idx}`,
      csiProductTaxRate: totalTaxRate,
      csiProductTaxType: item.csiProductTaxType || 'pt',
      csiProductTaxTotalValue: finalTotalWithTax.toFixed(2),
      csiHsnID: item.csiHsnID || item.hsnId || '0033f976-e897-4619-8921-d9aa2bce2a04',
      csiHsnCode: item.csiHsnCode || item.hsnCode || 'HSN-16',
      csiigstRate: igstRate || 0,
      csiigstValue: igstAmt || 0,
      csisgstRate: item.sgstRate || 0,
      csisgstValue: item.sgstAmt || 0,
      csicgstRate: item.cgstRate || 0,
      csicgstValue: item.cgstAmt || 0,
      csiOtherInfo: item.csiOtherInfo || {
        csiInfoReferenceType: item.otherInfo?.csiInfoReferenceType || item.csiProductName,
        csiInfoReferenceID: item.otherInfo?.csiInfoReferenceID || '5454545',
        csiinfoKey: item.otherInfo?.csiinfoKey || 'scds',
        csiinfoValue: item.otherInfo?.csiinfoValue || 'dvdf',
        csiInfoValueType: item.otherInfo?.csiInfoValueType || 'String',
        csiInfoRemarks: item.otherInfo?.csiInfoRemarks || 'oooo'
      }
    };
  });

  const transCharges = this.settingIsEnableTransportation() ? this.settingTransportationCharges() : 0;
  const basicAmt = this.totalBasicAmount();
  const discAmt = this.totalDiscountAmount();
  const netBeforeRound = this.netAmountBeforeRounding();
  const roundOff = this.roundingOffAmount();
  const netPayable = this.totalNetPayableAmount();
 
  const csiSummary: CounterSalesSummary = {
    poid: '4498bd43-0484-4c47-8c6a-a201f5906eba',
    csitotalQty: this.totalCartQty(),
    csitotalBacsicAmount: basicAmt,
    csitotalDiscountAmount: discAmt,
    csitotalChargeAmount: 0,
    csitotalDeductionAmount: 0,
    csitotalNetAmountBeforeRounding: netBeforeRound,
    csiroundingOffAmount: roundOff,
    csitotalNetPayableAmount: netPayable,
    csicurrencyCode: storeSetting?.defaultCurrency || 'India',
    csiexchangeRate: 1,
    csisummaryNote: null,
    csitotalTaxableAmount: this.taxableAmount(),
    csitotalCGSTAmount: 0,
    csitotalSGSTAmount: 0,
    csitotalIGSTAmount: 0,
    csitotalOtherTaxAmount: 0,
    csitotalAmount: this.tax(),
    csitotalTransportationCharges: transCharges,
    insertDate: nowIso,
    modifiedDate: nowIso
  };
console.log('this.settingDefaultSalesAccount()',this.settingDefaultSalesAccount())
  return {
    submitType,
    csiSalesAccountID: this.settingDefaultSalesAccountID()  || '',
    csiSalesAccountName: this.settingDefaultSalesAccount() || '',
    csiStockControlAccountID: this.settingStockControlAccountID() || '',
    csiStockControlAccountName: this.settingStockControlAccountName()  || '',
    csiTrnsDate: trnsDate,
    csiBranchID: this.settingBranch() || '',
    csiBranchState: storeSetting?.branchState || '',
    csiBranchName: this.settingBranchName() || '',
    csiLocationID: this.settingLocation() || '',
    csiLocationName: this.settingLocationName() || '',
    csiPartyID: this.settingDefaultCustomerID() || '',
    csiPartyState: this.settingPartyState() || '',
    csiPartyCode: customer?.partyCode || 'P-171',
    csiPartyTaxNumber: customer?.partyTaxNumber || '',
    csiIsRequirePartyPrice: false,
    csiPartyPriceID: '',
    csiNonTaxable: false,
    csiTaxTypeID: this.settingTaxTypeID() || '',
    csiPartyName: this.settingDefaultCustomer() || '',
    csiTaxTypeCode: storeSetting?.defaultTaxTypeCode || 'gst',
    csiProductRelationID: '',
    csiIsProductBatch: false,
    csiRemarks: '',
    csiInvoiceRequest: storeSetting?.isInvoiceRequest ?? true,
    csiIsEnableStorageBin: storeSetting?.isEnableStorageBin ?? false,
    csiIsEnableTransportation: storeSetting?.isEnableTransportation ?? true,
    csiIsEnableOtherInfo: false,
    csiIsDiscountOnSummary: storeSetting?.isDiscountOnSummary ?? false,
    csiRefDate: `${trnsDate}T00:00:00`,
    csiRefBillNo: billRef || `INV-${Date.now().toString().slice(-6)}`,
    csiRefDocUpload: 'dsdsds',
    csiRefDocUploadTag: 'sdsdsds',
    csiSummaryDiscountType: (this.globalDiscount()?.type === 'amount' ? 'VALUE' : 'PERCENTAGE'),
    csiSummaryDiscountValue: this.globalDiscount()?.value || 10,
    csiPartyPriceName: 'asasasa',
    csiTaxRateID: storeSetting?.defaultTaxRateID || 'taa-11',
    csiTaxRateValue: storeSetting?.defaultTaxRateValue ?? 87.9,
    csiTaxRateCode: storeSetting?.defaultTaxRateCode || 'ta-678',
    csiProductAutoPriceUpdate: false,
    csiPaymentTerms: storeSetting?.defaultPaymentTerms || 'assa',
    csiPartyShippingAddressid: customer?.shippingAddressId || storeSetting?.shippingAddressId || 'shipadd-12',
    csiPartyShippingLabelName: customer?.shippingLabelName || storeSetting?.shippingLabelName || 'shiplable-12',
    csiPartyShippingReceiverName: customer?.shippingReceiverName || storeSetting?.shippingReceiverName || 'shipRe-12',
    csiPartyBillingAddressId: customer?.billingAddressId || storeSetting?.billingAddressId || 'billad-12',
    csiPartyBillingLabelName: customer?.billingLabelName || storeSetting?.billingLabelName || 'billlab-12',
    csiPartyBillingReceiverName: customer?.billingReceiverName || storeSetting?.billingReceiverName || 'billre-12',
    csiValidUntilDate: null,
    csiIsCreditSales: storeSetting?.isCreditSales ?? true,
    CSISalesCashAccountName: this.settingDefaultCashAccount() || '',
    CSISalesCashAccountID: this.settingDefaultCashAccountID() || '',
    csiCounterOperatorID: operator?.id || storeSetting?.counterOperatorID || 'operator-01',
    csiCounterOperatorName: operator?.fullName || storeSetting?.counterOperatorName || 'Operator-ABC',
    csiPartyContactNumber: customer?.phone || 'PartyContact-05',
    csiPartyCouponCode: customer?.couponCode || 'partyCoupon-01',
    csiPartyMembershipID: customer?.membershipId || 'Member-01',
    csiPartyLayolityPoints: customer?.loyaltyPoints || 'points-001',
    csiIsNewParty: customer?.segment === 'New',
    insertedDate: nowIso,
    csiMasterID: payload.masterID,
    passKey: payload?.passKey,
    csiCompanyID: payload?.companyID, 
    csiSubscriptionID: payload?.subscriptionID,
    csiRoleID: payload?.roleID,
    csiProducts,
    csiSummary
  };
}


// 3. Await the payload inside initiatePayment
async initiatePayment(method: string) {
  if (this.cart().length === 0) {
    alert("Cart is empty! Cannot process payment.");
    return;
  }

  const storeSetting = this.storeSettingsService.getSettingForStore(this.selectedStore());
  if (!storeSetting) {
    this.isStoreConfiguredAlertOpen.set(true);
    return;
  }

  this.applyStoreSettingsDefaults(storeSetting);

  const cartItems = this.cart();
  const grossTotal = this.totalBasicAmount();
  const totalItemDiscount = this.totalItemDiscounts();

  const seq = Math.floor(1000 + Math.random() * 9000);
  const year = new Date().getFullYear();
  let invoiceRef = `CS-${year}-${seq}`;
  if (storeSetting.cashSalesInvoice === 'INV-CS-RETAIL') {
    invoiceRef = `RCS-${seq}`;
  } else if (storeSetting.cashSalesInvoice === 'INV-CS-TAX') {
    invoiceRef = `STI-${year}-${seq}`;
  } else if (storeSetting.cashSalesInvoice === 'INV-CS-EXPRESS') {
    invoiceRef = `QCS-${seq}`;
  }

  // Await the complete API payload object instead of passing an Observable
  const csiPayload = await this.buildCounterSalesInvoicePayload('SAVE', invoiceRef);
  this.lastCsiPayload.set(csiPayload);

  this.receiptDetails.set({
    method: method || storeSetting.defaultPaymentMode,
    id: 'TRX-' + Math.floor(Math.random() * 1000000).toString().padStart(6, '0'),
    date: new Date(),
    items: [...cartItems],
    grossTotal,
    totalItemDiscount,
    subtotal: this.subtotal(),
    tax: this.tax(),
    discount: this.globalDiscountAmount(),
    total: this.totalDue(),
    customer: this.currentCustomer(),
    cashSalesInvoiceRef: invoiceRef,
    cashAccount: storeSetting.defaultCashAccount,
    salesAccount: storeSetting.defaultSalesAccount,
    warehouse: storeSetting.defaultWarehouse,
    stockLocation: storeSetting.defaultStockLocation,
    priceList: storeSetting.defaultPriceList,
    taxCategory: storeSetting.defaultTaxCategory,
    currency: storeSetting.defaultCurrency,
    storeId: storeSetting.store,
    storeName: storeSetting.storeName,
    csiPayload,
    csiApiStatus: this.isOnline() ? 'SUCCESS' : 'OFFLINE_STORED',
    csiEndpoint: this.counterSalesApi.ENDPOINT,
    transportationCharges: this.settingIsEnableTransportation() ? this.settingTransportationCharges() : 0,
    roundingOff: this.roundingOffAmount()
  });

  this.isReceiptVisible.set(true);
  this.currentTabId.set(null);
  this.currentTabRef.set(null);
}

 

  async confirmPrint() {
    this.isPrinting.set(true);
    const completedTransaction = this.receiptDetails();
    if (!completedTransaction) return;

    // Call Backend TS API Integration: /api/LI210911101004_25EFCounterSalesInvoice/Insert
    if (completedTransaction.csiPayload) {
      try {
        const apiRes = await this.counterSalesApi.insertCounterSalesInvoice(
          completedTransaction.csiPayload,
          this.isOnline()
        );
        this.apiToastMessage.set({
          text: apiRes.message,
          type: apiRes.offlineStored ? 'warn' : 'success'
        });
        setTimeout(() => this.apiToastMessage.set(null), 4000);
      } catch (err: any) {
        console.error('API Insertion Error:', err);
        this.apiToastMessage.set({
          text: `Invoice stored offline: ${err?.message || 'Network unavailable'}`,
          type: 'warn'
        });
        setTimeout(() => this.apiToastMessage.set(null), 4000);
      }
    }

    // Dispatch print job to configured POS thermal printer
    try {
      const printJob = await this.posPrinter.printReceipt(completedTransaction);
      if (printJob.success) {
        this.showProductFeedback(`Printed receipt via "${printJob.printerName}"`, 'success');
      } else {
        this.showProductFeedback(`Print notice: ${printJob.message}`, 'warning');
      }
    } catch (e: any) {
      console.warn('Thermal print dispatch error:', e);
    }

    // Add loyalty points to the customer
    if (completedTransaction.customer && this.pointsEarned() > 0) {
      const customerId = completedTransaction.customer.id;
      const pointsToAdd = this.pointsEarned();
      let updatedCustomerRecord: Customer | undefined;

      this.customers.update(customers => {
        const customerIndex = customers.findIndex(c => c.id === customerId);
        if (customerIndex > -1) {
            const newCustomers = [...customers];
            const updatedCustomer = {
                ...newCustomers[customerIndex],
                points: newCustomers[customerIndex].points + pointsToAdd
            };
            newCustomers[customerIndex] = updatedCustomer;
            updatedCustomerRecord = updatedCustomer;
            return newCustomers;
        }
        return customers;
      });

      if (updatedCustomerRecord) {
          completedTransaction.customer = updatedCustomerRecord;
      }
    }

    this.sessionTransactions.update(history => [completedTransaction, ...history]);
    if (!this.isOnline()) {
        this.offlineQueue.update(q => [...q, completedTransaction]);
    }

    setTimeout(() => {
      this.isPrinting.set(false);
      this.isReceiptVisible.set(false);
      this.cart.set([]);
      this.selectedLineIndex.set(-1);
      this.setTab('lines');
      this.currentCustomer.set(null);
      this.globalDiscount.set(null);
      this.currentTabId.set(null);
      this.currentTabRef.set(null);
    }, 2500);
  }

  closeReceipt() { if (!this.isPrinting()) { this.isReceiptVisible.set(false); } }

  openCsiInspector(customPayload?: CounterSalesInvoicePayload) {
    if (customPayload) {
      this.lastCsiPayload.set(customPayload);
    } else if (this.cart().length > 0) {
      this.lastCsiPayload.set(this.buildCounterSalesInvoicePayload('SAVE'));
    } else if (!this.lastCsiPayload()) {
      this.lastCsiPayload.set(this.buildCounterSalesInvoicePayload('SAVE'));
    }
    this.isCsiInspectorModalOpen.set(true);
  }

  closeCsiInspector() {
    this.isCsiInspectorModalOpen.set(false);
  }

  async syncOfflineHeldBills() {
    this.isSyncing.set(true);
    const result = await this.counterSalesApi.syncOfflineHolds();
    this.isSyncing.set(false);
    this.lastSyncTime.set(new Date());
    this.apiToastMessage.set({
      text: `Sync completed: ${result.syncedCount} offline records verified with ${this.counterSalesApi.ENDPOINT}.`,
      type: 'success'
    });
    setTimeout(() => this.apiToastMessage.set(null), 3500);
  }

  toggleNetworkMode() {
    this.isOnline.update(v => !v);
    this.apiToastMessage.set({
      text: this.isOnline() ? 'Switched to Online mode.' : 'Switched to Offline Mode. Held bills will store in Offline Store.',
      type: this.isOnline() ? 'success' : 'warn'
    });
    setTimeout(() => this.apiToastMessage.set(null), 3000);
  }

  loadSampleCsiCustomer() {
    const cust = this.customers().find(c => c.name.includes('BHAGYALAXMI') || c.id === '006948b0-aef1-4447-854d-69892717f03f') || this.customers()[0];
    if (cust) {
      this.currentCustomer.set(cust);
      this.apiToastMessage.set({
        text: `Attached Party: ${cust.name} (${cust.partyCode || 'P-171'})`,
        type: 'info'
      });
      setTimeout(() => this.apiToastMessage.set(null), 2500);
    }
  }

  scanSampleCsiProduct() {
    this.processScanOrSearch('PARA-30339');
    this.apiToastMessage.set({
      text: 'Scanned Barcode PARA-30339: Water Pump Eicher',
      type: 'info'
    });
    setTimeout(() => this.apiToastMessage.set(null), 2500);
  }

  qtyModalAppend(char: string) {
    if (char === '.' && this.qtyModalValue().includes('.')) return;
    if (this.qtyModalValue().length > 5) return;
    this.qtyModalValue.update(v => v + char);
  }
  qtyModalBackspace() { this.qtyModalValue.update(v => v.slice(0, -1)); }
  qtyModalCancel() { this.isQtyModalOpen.set(false); }

  qtyModalConfirm() {
    const val = this.qtyModalValue();
    if (!val) { this.qtyModalCancel(); return; }
    const qty = parseFloat(val);
    if (!isNaN(qty) && qty > 0) {
      this.cart.update(items => {
        const newItems = [...items];
        const idx = this.selectedLineIndex();
        if (idx !== -1 && newItems[idx]) { newItems[idx] = { ...newItems[idx], qty }; }
        return newItems;
      });
    }
    this.isQtyModalOpen.set(false);
  }

  actionLoyalty() {
    this.loyaltyQuery.set('');
    this.isLoyaltyModalOpen.set(true);
    setTimeout(() => this.loyaltyInput?.nativeElement?.focus(), 50);
  }

  closeLoyaltyModal() { this.isLoyaltyModalOpen.set(false); }

  selectCustomer(customer: Customer) {
    this.currentCustomer.set(customer);
    this.isLoyaltyModalOpen.set(false);
  }

  detachCustomer() { if(confirm("Detach current customer?")) { this.currentCustomer.set(null); } }
  
  createCustomer() {
      const name = prompt("Enter Customer Name:");
      if(name) {
          const newCust: Customer = {
              id: `CUST-${Math.floor(Math.random()*1000)}`, name, phone: 'N/A', email: 'N/A', balance: 0, points: 0,
              initials: name.substring(0,2).toUpperCase(), segment: 'New', registeredAt: new Date()
          };
          this.selectCustomer(newCust);
      }
  }

  // --- Customer Management Methods ---
  setCustomerSegmentFilter(segment: 'All' | Customer['segment']) {
    this.customerListSegmentFilter.set(segment);
  }
  
  setCustomerSort(sortBy: 'name' | 'points' | 'newest') {
    this.customerListSort.set(sortBy);
  }
  
  selectCustomerForDetail(customer: Customer) {
    this.selectedCustomerForDetail.set(customer);
  }

  openCustomerModal(customer: Customer | null) {
      if (customer) {
          // Editing existing customer
          this.editingCustomer.set(customer);
          this.customerForm.set({ ...customer });
      } else {
          // Adding new customer
          this.editingCustomer.set(null);
          this.customerForm.set({
              id: '', name: '', phone: '', email: '', points: 0, balance: 0, initials: '', segment: 'New'
          });
      }
      this.isCustomerModalOpen.set(true);
  }

  closeCustomerModal() {
      this.isCustomerModalOpen.set(false);
      this.editingCustomer.set(null);
  }

  onCustomerFormInput(field: keyof Omit<Customer, 'id' | 'initials' | 'registeredAt'>, event: Event) {
    const input = event.target as HTMLInputElement | HTMLSelectElement;
    const value = input.type === 'number' ? +input.value : input.value;
    this.customerForm.update(f => ({ ...f, [field]: value }));
  }

  saveCustomer() {
      const formData = this.customerForm();
      if (!formData.name?.trim()) {
          alert("Customer name cannot be empty.");
          return;
      }

      if (this.editingCustomer()) {
          // Update existing customer
          this.customers.update(customers => 
              customers.map(c => {
                if (c.id === formData.id) {
                    const updatedCustomer = { ...c, ...formData } as Customer;
                    // If this is the currently selected customer, update the detail view as well
                    if (this.selectedCustomerForDetail()?.id === updatedCustomer.id) {
                        this.selectedCustomerForDetail.set(updatedCustomer);
                    }
                    return updatedCustomer;
                }
                return c;
              })
          );
      } else {
          // Add new customer
          const newCustomer: Customer = {
              id: `CUST-${Date.now().toString().slice(-6)}`,
              name: formData.name,
              phone: formData.phone || '',
              email: formData.email || '',
              points: formData.points || 0,
              balance: formData.balance || 0,
              segment: formData.segment || 'New',
              initials: formData.name.substring(0, 2).toUpperCase(),
              registeredAt: new Date()
          };
          this.customers.update(customers => [...customers, newCustomer]);
          // Select the new customer in the detail view
          this.selectedCustomerForDetail.set(newCustomer);
      }

      this.closeCustomerModal();
  }

  deleteCustomer(customerId: string) {
      if (confirm("Are you sure you want to delete this customer? This action is irreversible.")) {
          this.customers.update(customers => customers.filter(c => c.id !== customerId));
          
          if (this.selectedCustomerForDetail()?.id === customerId) {
            this.selectedCustomerForDetail.set(null);
          }
          // If the deleted customer is the one attached to the cart, detach them.
          if (this.currentCustomer()?.id === customerId) {
              this.currentCustomer.set(null);
          }
      }
  }


  actionUnit() {
    if (this.selectedLineIndex() === -1) { alert("Please select a line item first."); return; }
    this.isUnitModalOpen.set(true);
  }

  setLineUnit(unit: string) {
    this.cart.update(items => {
      const idx = this.selectedLineIndex();
      if (idx !== -1 && items[idx]) {
         const newItems = [...items];
         newItems[idx] = { ...newItems[idx], unit };
         return newItems;
      }
      return items;
    });
    this.isUnitModalOpen.set(false);
  }

  closeUnitModal() { this.isUnitModalOpen.set(false); }

  actionReturn() {
    const idx = this.selectedLineIndex();
    if (idx === -1) { alert("Please select a line item to mark as return."); return; }
    this.cart.update(items => {
        const newItems = [...items];
        const item = newItems[idx];
        newItems[idx] = { ...item, qty: item.qty * -1, discount: item.discount ? item.discount * -1 : undefined };
        return newItems;
    });
  }

  actionGiftCard() {
    this.giftCardMode.set('issue');
    this.giftCardCode.set('');
    this.giftCardAmount.set(0);
    this.isGiftCardModalOpen.set(true);
    setTimeout(() => this.giftCardInput?.nativeElement?.focus(), 50);
  }
  
  closeGiftCardModal() { this.isGiftCardModalOpen.set(false); }

  setGiftCardMode(mode: 'issue' | 'redeem') {
    this.giftCardMode.set(mode);
    this.giftCardCode.set('');
    this.giftCardAmount.set(0);
    setTimeout(() => this.giftCardInput?.nativeElement?.focus(), 50);
  }

  confirmGiftCard() {
    const code = this.giftCardCode(), amount = this.giftCardAmount();
    if (!code || amount <= 0) { alert("Please enter a valid card code and amount."); return; }
    const mode = this.giftCardMode(), isRedeem = mode === 'redeem';
    const finalPrice = isRedeem ? (amount * -1) : amount;
    const itemName = isRedeem ? `Redeem GC: ${code}` : `Issue GC: ${code}`;
    const newItem: any = {
      pr_ProductSkuID: 'GC-' + Math.floor(Math.random() * 10000), name: itemName, price: finalPrice, qty: 1, unit: 'ea', rep: 'SYS', category: 'GIFT_CARD'
    };
    this.cart.update(items => [...items, newItem]);
    if (!isRedeem) this.selectedLineIndex.set(this.cart().length - 1);
    this.isGiftCardModalOpen.set(false);
  }
  
// 2. Await the payload inside actionHoldBill
async actionHoldBill() {
  if (this.cart().length > 0) {
      const defaultName = this.currentCustomer()?.name || "Walk-in";
      let ref = this.currentTabRef();
      if (!this.currentTabId()) {
        const inputRef = prompt("Enter Name/Reference for this Tab:", defaultName);
        if (inputRef === null) return;
        ref = inputRef.trim() || defaultName;
      }

      const billId = this.currentTabId() || `HB-${Date.now().toString().slice(-6)}`;
      const heldBill: HeldBill = {
          id: billId,
          ticketRef: ref || defaultName,
          time: new Date(),
          customer: this.currentCustomer(),
          items: [...this.cart()],
          total: this.totalDue(),
          offlineHoldRecordId: billId
      };

      // Construct Counter Sales Invoice API HOLD payload by awaiting the promise
      const holdPayload = await this.buildCounterSalesInvoicePayload('HOLD', billId);
      holdPayload.csiRemarks = `Held Bill: ${ref}`;
      
      // Save to Offline Store for Hold Bill functionality via CounterSalesApiService
      await this.counterSalesApi.insertCounterSalesInvoice(holdPayload, this.isOnline());

      this.heldBills.update(bills => [heldBill, ...bills.filter(b => b.id !== heldBill.id)]);
      
      this.apiToastMessage.set({
        text: `Bill ${billId} held and saved to Offline Store (/api/LI210911101004_25EFCounterSalesInvoice/Insert)`,
        type: 'info'
      });
      setTimeout(() => this.apiToastMessage.set(null), 3500);

      this.clearScreenForNewOrder();
      return;
  }
  this.selectedHeldBillIndex.set(0);
  this.isHeldBillsModalOpen.set(true);
}

  actionVoid() {
      if(!this.currentUser()?.permissions.canVoid) {
          alert("You do not have permission to void transactions.");
          return;
      }
      if (this.cart().length > 0) {
          if (!confirm("Are you sure you want to VOID this transaction? This action is irreversible.")) return;
          const reason = prompt("Enter reason for voiding transaction (Required):");
          if (!reason) return;
          const voidedBill: VoidedBill = {
              id: `VOID-${Date.now().toString().slice(-6)}`, originalRef: this.currentTabRef() || 'Walk-in', time: new Date(), customer: this.currentCustomer(), items: [...this.cart()], total: this.totalDue(), reason: reason
          };
          this.voidedBills.update(bills => [voidedBill, ...bills]);
          this.clearScreenForNewOrder();
          return;
      }
      this.selectedVoidIndex.set(0);
      this.isVoidModalOpen.set(true);
  }

  restoreVoidedBill(id: string) {
      const bill = this.voidedBills().find(b => b.id === id);
      if (!bill) return;
      if (this.cart().length > 0 && !confirm("Clear active cart to restore voided bill?")) return;
      this.cart.set(bill.items);
      this.currentCustomer.set(bill.customer);
      this.selectedLineIndex.set(0);
      this.voidedBills.update(bills => bills.filter(b => b.id !== id));
      this.isVoidModalOpen.set(false);
  }
  
  closeVoidModal() { this.isVoidModalOpen.set(false); }

  clearScreenForNewOrder() {
    this.cart.set([]);
    this.currentCustomer.set(null);
    this.globalDiscount.set(null);
    this.selectedLineIndex.set(-1);
    this.currentTabId.set(null);
    this.currentTabRef.set(null);
  }

  resumeBill(billId: string) {
      const bill = this.heldBills().find(b => b.id === billId);
      if (!bill) return;
      if (this.cart().length > 0 && !confirm("Discard current active items to resume held bill?")) return;
      this.cart.set(bill.items);
      this.currentCustomer.set(bill.customer);
      this.selectedLineIndex.set(0);
      this.currentTabId.set(bill.id);
      this.currentTabRef.set(bill.ticketRef);
      this.heldBills.update(bills => bills.filter(b => b.id !== billId));

      // Remove from offline hold store on resume
      this.counterSalesApi.removeOfflineHoldBill(billId);

      this.isHeldBillsModalOpen.set(false);
  }

  startNewTab() {
      if (this.cart().length > 0 && !confirm("Clear current screen to start a new Tab?")) return;
      this.clearScreenForNewOrder();
      this.isHeldBillsModalOpen.set(false);
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 50);
  }

  discardHeldBill(billId: string, event: Event) {
      event.stopPropagation();
      if(confirm("Permanently discard this tab?")) {
        this.heldBills.update(bills => bills.filter(b => b.id !== billId));
        this.counterSalesApi.removeOfflineHoldBill(billId);
      }
  }
  
  closeHeldBillsModal() { this.isHeldBillsModalOpen.set(false); }

  actionNumPad() {
    this.numPadValue.set('');
    this.isNumPadOpen.set(true);
  }

  closeNumPad() { this.isNumPadOpen.set(false); }
  numPadAppend(key: string) {
    if (key === '.' && this.numPadValue().includes('.')) return;
    if (this.numPadValue().length > 8) return;
    this.numPadValue.update(v => v + key);
  }
  numPadBackspace() { this.numPadValue.update(v => v.slice(0, -1)); }
  numPadClear() { this.numPadValue.set(''); }

  numPadConfirm() {
    const val = parseFloat(this.numPadValue());
    if (val > 0) {
        const newItem: any = {
            sku: 'CUST-' + Date.now().toString().slice(-4), name: 'Custom Amount', price: val, qty: 1, unit: 'ea', rep: 'MANUAL', category: 'General'
        };
        this.addToCart(newItem);
        this.closeNumPad();
    }
  }

  actionDiscount() {
      if(!this.currentUser()?.permissions.canDiscount) {
          alert("You do not have permission to apply discounts.");
          return;
      }
      this.discountModalTab.set(this.selectedLineIndex() !== -1 ? 'line' : 'global');
      this.discountModalValue.set('0');
      this.discountModalType.set('percent');
      this.isDiscountModalOpen.set(true);
  }

  setDiscountType(type: 'percent' | 'amount') { this.discountModalType.set(type); }
  setDiscountTab(tab: 'line' | 'global') {
      this.discountModalTab.set(tab);
      this.discountModalValue.set('0');
  }

  appendDiscountChar(char: string) {
      if (char === '.' && this.discountModalValue().includes('.')) return;
      if (this.discountModalValue() === '0' && char !== '.') { this.discountModalValue.set(char); } 
      else { this.discountModalValue.update(v => v + char); }
  }

  backspaceDiscount() { this.discountModalValue.update(v => v.length > 1 ? v.slice(0, -1) : '0'); }

  confirmDiscount() {
    
      const val = parseFloat(this.discountModalValue()), type = this.discountModalType();
      if (this.discountModalTab() === 'line') {
          const idx = this.selectedLineIndex();
          if (idx !== -1) {
              this.cart.update(items => {
                
                  const newItems = [...items], item = newItems[idx];
                  // let discountAmount = type === 'amount' ? val : (item.pr_DefaultSalesMRPPrice * item.qty) * (val / 100);
                  let basePrice = 0;
                  if (item?.selectedPriceOption === 'cost') {
                    basePrice = item?.costPrice || 0;
                  } else if (item?.selectedPriceOption === 'sales') {
                    basePrice = item?.salesPrice?.pr_DefaultSalesPrice || item?.salesPrice || 0;
                  } else if (item?.selectedPriceOption === 'mrp') {
                    basePrice = item?.mrpPrice || item?.salesPrice?.pr_DefaultSalesMRPPrice || 0;
                  } else {
                    // Fallback order if no matching option or unselected
                    basePrice = item?.salesPrice || item?.salesPrice?.pr_DefaultSalesPrice || item?.mrpPrice || item?.costPrice || 0;
                  }

                let discountAmount = type === 'amount' ? val : (basePrice * item.qty) * (val / 100);
                  newItems[idx] = { ...item, discount: discountAmount,csiDiscountType:type,csiProductDiscount:val };
                  console.log('newItem1',newItems)
                  return newItems;
              });
          }
      } else {
          this.globalDiscount.set(val > 0 ? { type, value: val } : null);
      }
      
      this.closeDiscountModal();
  }
  // confirmDiscount() {
  //   
  //   const val = parseFloat(this.discountModalValue());
  //   const type = this.discountModalType(); // 'amount' or 'percentage'
  
  //   if (isNaN(val) || val < 0) {
  //     this.closeDiscountModal();
  //     return;
  //   }
  
  //   if (this.discountModalTab() === 'line') {
  //     const idx = this.selectedLineIndex();
  
  //     if (idx !== -1) {
  //       this.cart.update(items => {
  //         const newItems = [...items];
  //         const item = newItems[idx];
  
  //         if (!item) return newItems;
  //         
  //         // Dynamic price resolution from Product structure
  //         const unitPrice = item?.salesPrice || item?.salesPrice.pr_DefaultSalesPrice || item?.mrpPrice || item?.costPrice || 0;
  //          const lineTotal = unitPrice * (item.qty ?? 1);
          
  //         // Calculate line discount amount
  //         const discountAmount = type === 'amount' 
  //           ? val 
  //           : lineTotal * (val / 100);
  
  //         // Build dynamic SalesDiscountBreakup record
  //         const discountBreakup: SalesDiscountBreakup = {
  //           pr_SalesDiscountID: `DISC-${Date.now()}`,
  //           discountLevel: 'LINE',
  //           discountType: type === 'amount' ? 'FLAT' : 'PERCENTAGE',
  //           discountValue: val,
  //           discountRemarks: `Applied ${type === 'amount' ? 'Flat' : val + '%'} discount`
  //         };
  
  //         // Update item with dynamic discount properties and salesPrice breakup sync
  //         newItems[idx] = {
  //           ...item,
  //           discountType:type,
  //           productDiscount:val,
  //           discount: discountAmount,////////////////////////////productDiscountValue
  //          productGrossDiscountValue:'',
  //           salesPrice: {
  //             ...(item.salesPrice ?? {} as SalesPrice),
  //             pr_DefaultSalesPrice: unitPrice,
  //             pr_IsDiscountEnabledOnSales: val > 0,
  //             salesDiscountBreakups: val > 0 ? [discountBreakup] : []
  //           }
  //         };
  
  //         return newItems;
  //       });
  //     }
  //   } else {
  //     // Global discount handling
  //     this.globalDiscount.set(val > 0 ? { type, value: val } : null);
  //   }
  
  //   this.closeDiscountModal();
  // }

  closeDiscountModal() { this.isDiscountModalOpen.set(false); }
  toggleTheme() { this.isDarkMode.update(v => !v); }
  toggleRightPanel() { this.isRightPanelCollapsed.update(v => !v); }
  toggleLowStockAlert() { this.isLowStockAlertEnabled.update(v => !v); }
  setTab(tab: 'lines' | 'payments' | 'online') { this.activeTab.set(tab); }
  toggleOrderExpansion(orderId: string) { this.expandedOrderId.update(current => current === orderId ? null : orderId); }

  generateBulkData() {
    if(!confirm("Load 1000 items into cart? This may take a moment.")) return;
    const items: any[] = Array.from({length: 1000}, (_, i) => {
       const id = 100000 + i;
       return { sku: id.toString(), name: `Bulk Product Item #${id}`, price: parseFloat((Math.random() * 50 + 5).toFixed(2)), qty: 1, unit: 'ea', rep: 'BULK', category: 'TEST' };
    });
    this.cart.set(items);
    this.activeTab.set('lines');
  }

  // addToCart(product: Product) {
  //   const salesPrice = product.salesPrice ?? product.price;
  //   const mrpPrice = product.mrpPrice ?? salesPrice;
  //   const wholesalePrice = product.wholesalePrice ?? (salesPrice * 0.85);
  //   const costPrice = product.costPrice ?? (salesPrice * 0.7);

  //   const priceOptions: ProductPriceOption[] = product.priceOptions && product.priceOptions.length > 0
  //     ? product.priceOptions
  //     : [
  //         { id: 'sales', name: 'Sales Price', price: salesPrice, isDefault: true },
  //         { id: 'mrp', name: 'MRP', price: mrpPrice },
  //         { id: 'wholesale', name: 'Wholesale Tier', price: wholesalePrice },
  //         { id: 'cost', name: 'Cost Price', price: costPrice }
  //       ];

  //   this.cart.update(items => {
  //     const existingIdx = items.findIndex(i => i.sku === product.sku);
  //     if (existingIdx > -1) {
  //       const newItems = [...items];
  //       const itemToUpdate = newItems[existingIdx];
  //       const newQty = itemToUpdate.qty + 1;
        
  //       let newDiscount: number | undefined = undefined;
  //       if (product.discountRule && product.discountRule.value > 0) {
  //           if (product.discountRule.type === 'amount') {
  //               newDiscount = product.discountRule.value * newQty;
  //           } else {
  //               newDiscount = (itemToUpdate.price * (product.discountRule.value / 100)) * newQty;
  //           }
  //       }
  //       newItems[existingIdx] = { ...itemToUpdate, qty: newQty, discount: newDiscount };
  //       return newItems;

  //     } else {
  //       const newItem: CartItem = { 
  //         ...product, 
  //         qty: 1, 
  //         unit: product.uomName || 'ea',
  //         price: salesPrice,
  //         salesPrice: salesPrice,
  //         mrpPrice: mrpPrice,
  //         wholesalePrice: wholesalePrice,
  //         costPrice: costPrice,
  //         priceOptions: priceOptions,
  //         selectedPriceOption: product.selectedPriceOption || 'sales'
  //       };

  //       if (product.discountRule && product.discountRule.value > 0) {
  //           if (product.discountRule.type === 'amount') {
  //              newItem.discount = product.discountRule.value; // for qty 1
  //          } else {
  //              newItem.discount = salesPrice * (product.discountRule.value / 100); // for qty 1
  //          }
  //       }
  //       this.selectedLineIndex.set(0);
  //       return [newItem, ...items];
  //     }
  //   });
  //   if (this.activeTab() !== 'lines') this.activeTab.set('lines');
  // }

  // updateQty(index: number, delta: number, event?: Event) {
  //   event?.stopPropagation();
  //   this.cart.update(items => {
  //     const newItems = [...items];
  //     const item = newItems[index];
  //     const newQty = item.qty + delta;

  //     if (newQty <= 0) {
  //       newItems.splice(index, 1);
  //       if (this.selectedLineIndex() === index) { this.selectedLineIndex.set(-1); }
  //       else if (this.selectedLineIndex() > index) { this.selectedLineIndex.update(v => v - 1); }
  //     } else { 
  //       const originalProduct = this.productCatalog()[item.sku]; // Get latest rule
  //       let newDiscount: number | undefined = undefined;
  //       if (originalProduct?.discountRule && originalProduct.discountRule.value > 0) {
  //           if (originalProduct.discountRule.type === 'amount') {
  //               newDiscount = originalProduct.discountRule.value * newQty;
  //           } else {
  //               // Use the price from the cart item, not the current catalog price, for consistency
  //               newDiscount = (item.pr_DefaultSalesMRPPrice * (originalProduct.discountRule.value / 100)) * newQty;
  //           }
  //       }
  //       newItems[index] = { ...item, qty: newQty, discount: newDiscount };
  //     }
  //     return newItems;
  //   });
  // }

  // selectLine(index: number) { this.selectedLineIndex.set(index); }

  // actionAddDiscount(event: Event) {
  //   event.stopPropagation();
  //   this.selectLine(this.selectedLineIndex());
  //   this.actionDiscount();
  // }

  // addToCart(product: any) {
  //   
  //   this.cart.update(items => {
      
  //     // Unique identifier fallback: SkuID -> ProductID
  //     const productKey =product?.pr_ProductID;
      
  //     const existingIdx = items.findIndex(
  //       i => (i?.pr_ProductID) === productKey
  //     );
  
  //     if (existingIdx > -1) {
  //       const newItems = [...items];
  //       const itemToUpdate = newItems[existingIdx];
  //        const newQty = (itemToUpdate?.qty ?? 0) + 1;
       
  //       // Recalculate line discount based on dynamic salesPrice breakdown
  //       const newDiscount = this.calculateDynamicDiscount(itemToUpdate, newQty);
  
  //       newItems[existingIdx] = { 
  //         ...itemToUpdate, 
  //          qty: newQty, 
  //         discount: newDiscount 
  //       };
  //       return newItems;
  //     } else {
  //       // Map basic CartItem extensions
  //       const newItem: CartItem = { 
  //         ...product, 
  //         qty: 1, 
  //         unit: product?.pr_BaseUOMName || 'ea' 
  //       };
  
  //       newItem.discount = this.calculateDynamicDiscount(newItem, 1);
  
  //       this.selectedLineIndex.set(0);
  //       return [newItem, ...items];
  //     }
  //   });
  
  //   if (this.activeTab() !== 'lines') {
  //     this.activeTab.set('lines');
  //   }
  // }
  addToCart(product: any) {
    
    const val = parseFloat(this.discountModalValue()), type = this.discountModalType();
    this.cart.update(items => {
      
      // Unique identifier fallback: SkuID -> ProductID
      const productKey = product?.pr_ProductID || product?.csiProductID;
      
      const existingIdx = items.findIndex(
        i => (i?.pr_ProductID || i?.csiProductID) === productKey
      );
  
      if (existingIdx > -1) {
        const newItems = [...items];
        const itemToUpdate = newItems[existingIdx];
        const newQty = (Number(itemToUpdate?.qty ?? itemToUpdate?.csiProductQty) || 0) + 1;
       
        // Recalculate line discount based on dynamic salesPrice breakdown
        const newDiscount = this.calculateDynamicDiscount(itemToUpdate, newQty);
  
        const grossVal = newQty * Number(itemToUpdate.salesPrice || itemToUpdate.salesPrice || 0);
        const totalVal = grossVal - Number(newDiscount || 0);
  
        newItems[existingIdx] = { 
          ...itemToUpdate, 
          qty: newQty,
          csiProductQty: String(newQty),
          discount: newDiscount,
          csiProductGrossValue: String(grossVal),
          csiProductTotalValue: String(totalVal)
        };
        return newItems;
      } else {
        
        console.log('product',product)
        // Map product values matching the target csiProducts structure schema
        const costPrice = Number(product?.salesPrice  || 0);
        const mrpPrice = Number(product?.iteme?.salesPrice?.currency  || 0);
        const initialQty = 1;
        const grossVal = initialQty * costPrice;
        const discountVal = Number(product?.discount || 0);
        
        const totalVal = grossVal - discountVal;
        
        let binData = product?.item?.productStorageBinStockConfigs ?? [];
        let altUOMData =  product?.item?.alternateUomSalesPrices ?? [];
        const newItem: any = {
          // Core identifiers and mapping
          csipid: product?.item?.csipid || product?.pr_ProductID || '4868D54C-A0D7-4A97-BF7C-ED2DE5E52C7B',
          csiProductID:product?.item?.pr_ProductID || product?.csiProductID || '',
          csiProductCode: product?.item?.csiProductCode || '',
          csiIsBatch: product?.item?.csiIsBatch ?? false,
          csiProductName: product?.item?.pr_ProductName || product?.csiProductName || '',
          csiProductDesc: product?.item?.productDesc || product?.csiProductDesc || '',
          csiLocationID: product?.item?.csiLocationID || null,
          csiLocationName: product?.item?.csiLocationName || null,
          // Category & Brand info
          csiProductCategoryID: product?.item?.productCategory?.categoryID || product?.csiProductCategoryID || '',
          csiProductCategoryName: product?.item?.productCategory?.categoryName || product?.csiProductCategoryName || '',
          csiProductBrandID:product?.item?.productBrand?.brandID || product?.csiProductBrandID || '',
          csiProductBrandName:product?.productBrand?.brandName || product?.csiProductBrandName || '',
  
          // UOM configuration
          csiProductUomID: product?.item?.productUOM?.baseUOMID || product?.csiProductUomID || '',
          csiProductUomName:product?.item?.productUOM?.baseUOMName || product?.unit || product?.csiProductUomName || 'ea',
          csiProductIsAltUomEnabled: product?.item?.pr_IsAltUomEnabled ?? false,
          csiProductAltUserUOMID: altUOMData[0]?.pr_AltUserUOMID || null,
          csiProductAltUserUOMName: altUOMData[0]?.pr_AltUserUOMName || null,
          csiProductQtyInSetBase:  altUOMData[0]?.pr_QtyInSetBase || 1,
         
          csiStorageBinID: binData[0]?.storageBinID || null,
          csiStorageBinName: binData[0]?.binName || null,
          csiMinStockLevel: binData[0]?.minStockLevel || null,
          csiMaxStockLevel: binData[0]?.maxStockLevel || null,
  
          // Pricing and Financial calculations
          csiProductQty: String(initialQty),
          csiProductCostPrice: String(costPrice),
          csiProductMrpPrice: String(mrpPrice),
          csiDiscountType: type || 'VALUE',
          csiProductDiscount: String(discountVal),
          csiProductGrossValue: String(grossVal),
          csiProductDiscountValue: "0",
          csiProductGrossDiscountValue: String(discountVal),
          csiProductTotalValue: String(totalVal),
          csiIsIncTax: product?.csiIsIncTax ?? false,
          csiProductPriceEffectDate: null,
          csiProductRemarks: product?.remarks || product?.csiProductRemarks || '',
          csiid: product?.csiid || product?.rep || '',
  
          // Tax configuration
          csiProductTaxRate: product?.item?.productHSN?.gstRate || 0,
          csiProductTaxValue: product?.item?.csiProductTaxValue || 0,
          csiProductTaxableValue: product?.item?.csiProductTaxableValue || 0,
          csiProductTaxType: product?.item?.csiProductTaxType || 'GST',
          csiProductTaxTotalValue: product?.item?.csiProductTaxTotalValue || 0,
          csiHsnID: product?.item?.productHSN?.pr_HSNID || product?.csiHsnID || '',
          csiHsnCode: product?.item?.productHSN?.pr_HSNCode || product?.csiHsnCode || '',
          csiigstRate: product?.item?.productHSN?.igst || 0,
          csiigstValue: product?.item?.csiigstValue || 0,
          csisgstRate:product?.item?.productHSN?.sgst || 0,
          csisgstValue: product?.item?.csisgstValue || 0,
          csicgstRate: product?.item?.productHSN?.cgst || 0,
          csicgstValue: product?.item?.csicgstValue || 0,
          csiChargesAndDeductions: product?.csiChargesAndDeductions || null,
  
          // Optional Extra Sub-objects
          csiOtherInfo: product?.csiOtherInfo || {
            csipid: product?.item?.csipid || product?.item?.pr_ProductID || '',
            csiOtherInfoID: '',
            csiInfoReferenceType: product?.item?.pr_ProductName || '',
            csiInfoReferenceID: '',
            csiInfoKey: '',
            csiInfoValue: '',
            csiInfoValueType: 'String',
            csiInfoRemarks: ''
          },
          csiSummaryAltUom: product?.item?.csiSummaryAltUom || null,
  
          // Retain compatibility fields used across UI components
          qty: initialQty,
          unit: product?.uomName || product?.unit || 'ea',
          discount: discountVal,
          ...product
        };
  
        newItem.discount = this.calculateDynamicDiscount(newItem, 1);
  console.log('newItem',newItem)
        this.selectedLineIndex.set(0);
        return [newItem, ...items];
      }
    });
  
    if (this.activeTab() !== 'lines') {
      this.activeTab.set('lines');
    }
  }
  
  updateQty(index: number, delta: number, event?: Event) {
    event?.stopPropagation();
  
    this.cart.update(items => {
      const newItems = [...items];
      const item = newItems[index];
      if (!item) return items;
  
      const newQty = (item.qty ?? 0) + delta;
     
      if (newQty <= 0) {
        newItems.splice(index, 1);
        
        if (this.selectedLineIndex() === index) { 
          this.selectedLineIndex.set(-1); 
        } else if (this.selectedLineIndex() > index) { 
          this.selectedLineIndex.update(v => v - 1); 
        }
      } else { 
        const productKey =  item?.pr_ProductID;
        
        // Look up original product in catalog if available, or fall back to item
        const originalProduct = this.productCatalog()?.[productKey] || item;
        
        const newDiscount = this.calculateDynamicDiscount(originalProduct, newQty, item);
  
        newItems[index] = { 
          ...item, 
           qty: newQty, 
           discount: newDiscount 
        };
      }
      return newItems;
    });
    console.log('this.cart()',this.cart())
  }
  private calculateDynamicDiscount(
    product: Product | CartItem, 
    qty: number, 
    fallbackCartItem?: CartItem
  ): number | undefined {
    const salesPriceObj = product.salesPrice ?? fallbackCartItem?.salesPrice;
    const unitPrice = salesPriceObj?.pr_DefaultSalesPrice ?? 0;
  
    if (!salesPriceObj?.pr_IsDiscountEnabledOnSales) {
      return undefined;
    }
  
    const primaryBreakup = salesPriceObj.salesDiscountBreakups?.[0];
    if (!primaryBreakup || !primaryBreakup.discountValue) {
      return undefined;
    }
  
    const isAmount = primaryBreakup.discountType?.toUpperCase() === 'FLAT' 
      || primaryBreakup.discountType?.toUpperCase() === 'AMOUNT';
  
    if (isAmount) {
      return primaryBreakup.discountValue * qty;
    } else {
      return (unitPrice * (primaryBreakup.discountValue / 100)) * qty;
    }
  }
  selectLine(index: number) { this.selectedLineIndex.set(index); }

  actionAddDiscount(event: Event) {
    event.stopPropagation();
    this.selectLine(this.selectedLineIndex());
    this.actionDiscount();
  }

 

  actionAddNote(event: Event) {
    event.stopPropagation();
    const idx = this.selectedLineIndex();
    if (idx === -1) return;
    const currentNote = this.cart()[idx].note || '';
    const val = prompt('Enter line note:', currentNote);
    if (val !== null) {
      this.cart.update(items => {
        const newItems = [...items];
        newItems[idx] = { ...newItems[idx], note: val };
        return newItems;
      });
    }
  }

  actionRemoveLine(event: Event) {
    event.stopPropagation();
    const idx = this.selectedLineIndex();
    if (idx === -1) return;
    this.cart.update(items => {
      const newItems = [...items];
      newItems.splice(idx, 1);
      return newItems;
    });
    this.selectedLineIndex.set(-1);
  }
  
  handleTileAction(actionId: string) {
      switch(actionId) {
          case 'qty': this.actionSetQty(); break;
          case 'loyalty': this.actionLoyalty(); break;
          case 'unit': this.actionUnit(); break;
          case 'return': this.actionReturn(); break;
          case 'gift': this.actionGiftCard(); break;
          case 'hold': this.actionHoldBill(); break;
          case 'void': this.actionVoid(); break;
          case 'tax': alert('Tax override feature not implemented in this demo.'); break;
          case 'stock': this.actionStockCheck(); break;
          case 'numpad': this.actionNumPad(); break;
          case 'discount': this.actionDiscount(); break;
          default: console.warn('Unknown action:', actionId);
      }
  }

  actionSetQty() {
    const idx = this.selectedLineIndex();
    if (idx === -1) {
       if (this.cart().length > 0) this.selectLine(0); else return;
    }
    this.qtyModalValue.set('');
    this.isQtyModalOpen.set(true);
  }

  actionStockCheck() {
    this.stockSearchQuery.set('');
    this.stockSelectedCategory.set(null);
    this.stockSelectedIndex.set(0);
    this.isStockModalOpen.set(true);
    setTimeout(() => this.stockInput?.nativeElement?.focus(), 50);
  }

  closeStockModal() { this.isStockModalOpen.set(false); }

  selectStockCategory(category: string | null) {
      this.stockSelectedCategory.set(category);
      this.stockSelectedIndex.set(0);
  }

  addStockItemToCart(item: Product) { this.addToCart(item); }

  getTotalStock(sku: string): number {
      const records = this.stockMap()[sku];
      if (!records) return 0;
      return records.reduce((sum, r) => sum + (r.onHand - r.reserved), 0);
  }

  handleSearchEnter(event: Event) {
    const input = event.target as HTMLInputElement;
    const val = input.value.trim();
    if (!val) return;
    this.processScanOrSearch(val);
    input.value = '';
    this.searchQuery.set('');
  }

  showProductFeedback(text: string, type: 'success' | 'info' | 'warning' = 'info') {
    if (this.feedbackTimeout) clearTimeout(this.feedbackTimeout);
    this.productFeedback.set({ text, type });
    this.feedbackTimeout = setTimeout(() => {
      this.productFeedback.set(null);
    }, 4500);
  }

  openPriceOptionModal(item: CartItem, index: number, event?: Event) {
    
    if (event) event.stopPropagation();
    this.priceOptionSelectedItem.set(item);
    this.priceOptionSelectedLineIndex.set(index);
    this.isPriceOptionModalOpen.set(true);
  }

  closePriceOptionModal() {
    this.isPriceOptionModalOpen.set(false);
    this.priceOptionSelectedItem.set(null);
    this.priceOptionSelectedLineIndex.set(-1);
  }

  selectPriceOption(option: ProductPriceOption) {
    
    const idx = this.priceOptionSelectedLineIndex();
    if (idx === -1) return;

    this.cart.update(items => {
      if (!items[idx]) return items;
      const copy = [...items];
      const target = { ...copy[idx] };
      target.price = option.price;
      target.selectedPriceOption = option.id;

      if (target.discountRule && target.discountRule.value > 0) {
        if (target.discountRule.type === 'amount') {
          target.discount = target.discountRule.value * target.qty;
        } else {
          target.discount = (target.price * (target.discountRule.value / 100)) * target.qty;
        }
      }

      copy[idx] = target;
      return copy;
    });

    const currentItem = this.priceOptionSelectedItem();
    this.showProductFeedback(`Price tier changed to "${option.name}" ($${option.price.toFixed(2)}) for ${currentItem?.pr_ProductName || 'item'}`, 'success');
    this.closePriceOptionModal();
  }

  async syncProductsFromApi(isInitial = false) {
    try {
      const items:any[] = await this.productLookup.fetchProductList({
        pageNumber: this.catalogPageNumber(),
        pageSize: this.catalogPageSize(),
        searchContent: this.catalogSearchQuery() || null,
        pr_CategoryID: this.catalogSelectedCategory(),
        pr_BrandID: this.catalogSelectedBrand(),
        
      });

      if (items && items.length > 0) {
        // this.productCatalog.update(cat => {
        //   const updated = { ...cat };
        //   for (const item of items) {
        //     const posProd = this.productLookup.toPosProduct(item);
        //     updated[posProd.sku] = posProd;
        //   }
        //   return updated;
        // });
        
        const catalogRecord: Record<string, Product> = (items || []).reduce((acc, product) => {
          const key = product?.pr_ProductCode || product?.pr_ProductID;
          if (key) {
            acc[key] = product;
          }
          return acc;
        }, {} as Record<string, Product>);
  console.log('catalogRecord',catalogRecord)
        // Update the productCatalog signal dynamically with API data
        this.productCatalog.set(catalogRecord);

        // Update stock map
        this.stockMap.update(m => {
          const newMap = { ...m };
          for (const item of items) {
            const sku = item.sku || item.productCode || item.barcode || '';
            if (sku && !newMap[sku]) {
              newMap[sku] = [{ location: 'Main Store', onHand: Number(item.totalStock) || 100, reserved: 0 }];
            }
          }
          return newMap;
        });

        if (!isInitial) {
          this.showProductFeedback(`Synced ${items.length} products from Inventory API.`, 'success');
        }
      } else if (!isInitial) {
        this.showProductFeedback('No products returned from Inventory API.', 'info');
      }
    } catch (err: any) {
      if (!isInitial) {
        this.showProductFeedback('Failed to sync products from Inventory API.', 'warning');
      }
    }
  }

  async processScanOrSearch(code: string) {
    const query = (code || '').trim();
    if (!query) return;
  
    this.isSearchingProduct.set(true);
    
    try {
      const productCatalog = this.productCatalog();
      const searchKey = query;
  
      // Find if there is a local product mapping to grab its ID if available, otherwise pass query or null
      const matchedLocalProduct = productCatalog[searchKey] || 
        (Object.values(productCatalog) as Product[]).find(p => 
          //( p?.barCodeBreakups[0]?.barCodeValue.toLowerCase().includes(searchKey.toLowerCase())) ||
          p.pr_ProductName?.toLowerCase().includes(searchKey.toLowerCase()) || 
          p.pr_ProductCode?.toLowerCase().includes(searchKey.toLowerCase()) || 
          p.pr_ProductTitle?.toLowerCase().includes(searchKey.toLowerCase())  
        );

        
  
      // ✅ 1. Always call fetchProductGetByID first using await
      const apiItem: any = await this.productLookup.fetchProductGetByID({
        pageNumber: this.catalogPageNumber(),
        pageSize: this.catalogPageSize(),
        searchContent: query, // Pass scanned/searched query or fallback to catalog query
        pr_CategoryID: this.catalogSelectedCategory(),
        pr_BrandID: this.catalogSelectedBrand(),
        Pr_ProductID: matchedLocalProduct?.pr_ProductID || null,
      });
  
      if (apiItem) {
       // const converted: Product = this.productLookup.toPosProduct(apiItem);
       const converted: Product = this.productLookup.toPosProduct(apiItem);
        // Add to productCatalog so it is dynamically registered in catalog
        
        this.productCatalog.update(cat => ({ ...cat, [converted.pr_ProductSkuID]: converted }));
        
        // Update stock
        this.stockMap.update(m => ({
          ...m,
          [converted.pr_ProductSkuID]: [{ location: 'Main Store', onHand: Number(apiItem.totalStock) || 100, reserved: 0 }]
        }));
        
        this.addToCart(converted,);
        this.showProductFeedback(`Dynamic Scan: Loaded "${converted.pr_ProductName}" (Sales Price: $${converted.salesPrice?.pr_DefaultSalesMRPPrice?.toFixed(2)})`, 'success');
        return;
      }
  
      // 2. Direct key match in local catalog (Fallback if API returned nothing)
      if (productCatalog[searchKey]) {
        this.addToCart(productCatalog[searchKey]);
        this.showProductFeedback(`Loaded "${productCatalog[searchKey].pr_ProductName}" (Sales Price: $${((productCatalog[searchKey].salesPrice?.pr_DefaultSalesMRPPrice)).toFixed(2)})`, 'success');
        return;
      }
  
      // 3. Exact Barcode or case-insensitive SKU match in local catalog
      const localMatch = matchedLocalProduct || (Object.values(productCatalog) as Product[]).find(p => 
        (p?.pr_ProductSkuID && p?.pr_ProductSkuID.toLowerCase() === query.toLowerCase()) ||
        (p?.barCodeBreakups?.[0]?.barCodeValue && p?.barCodeBreakups[0]?.barCodeValue.toLowerCase() === query.toLowerCase()) ||
        (p?.pr_ProductName && p?.pr_ProductName.toLowerCase() === query.toLowerCase())
      );
  
      if (localMatch) {
        this.addToCart(localMatch);
        this.showProductFeedback(`Loaded "${localMatch.pr_ProductName}" (Sales Price: $${(localMatch.salesPrice?.pr_DefaultSalesMRPPrice).toFixed(2)})`, 'success');
        return;
      }
  
      // 4. Fuzzy search in catalog by name
      const fuzzyMatch = (Object.values(productCatalog) as Product[]).find(p => 
        p.pr_ProductName.toLowerCase().includes(query.toLowerCase())
      );
  
      if (fuzzyMatch) {
        this.addToCart(fuzzyMatch);
        this.showProductFeedback(`Loaded "${fuzzyMatch.pr_ProductName}" (Sales Price: $${(fuzzyMatch.salesPrice?.pr_DefaultSalesMRPPrice).toFixed(2)})`, 'info');
        return;
      }
  
      this.showProductFeedback(`No product found for "${query}". Check barcode or sync Product Catalogue.`, 'warning');
    } catch (err: any) {
      console.error('Dynamic product search error:', err);
      this.showProductFeedback(`Product lookup error for "${query}".`, 'warning');
    } finally {
      this.isSearchingProduct.set(false);
    }
  }

  async searchCatalogFromApi() {
    const q = this.stockSearchQuery().trim();
    this.catalogSearchQuery.set(q);
    this.catalogPageNumber.set(1);
    await this.syncProductsFromApi();
  }

  async changeCatalogPageSize(event: Event) {
    const select = event.target as HTMLSelectElement;
    const size = parseInt(select.value, 10) || 10;
    this.catalogPageSize.set(size);
    this.catalogPageNumber.set(1);
    await this.syncProductsFromApi();
  }

  async nextCatalogPage() {
    this.catalogPageNumber.update(p => p + 1);
    await this.syncProductsFromApi();
  }

  async prevCatalogPage() {
    if (this.catalogPageNumber() > 1) {
      this.catalogPageNumber.update(p => p - 1);
      await this.syncProductsFromApi();
    }
  }

  // --- Product Catalog Management ---
  updateProductField(sku: string, field: 'price' | 'discountType' | 'discountValue', event: Event) {
    const input = event.target as HTMLInputElement | HTMLSelectElement;
    const value = input.value;

    this.productCatalog.update(catalog => {
        const productToUpdate = catalog[sku];
        
        if (!productToUpdate) {
            console.error(`Product with SKU ${sku} not found.`);
            return catalog;
        }

        const newCatalog = { ...catalog };
        const updatedProduct = { ...productToUpdate }; // Create a shallow copy to modify

        if (field === 'price') {
            updatedProduct.salesPrice.pr_DefaultSalesMRPPrice = parseFloat(value) || 0;
        } else {
            // Ensure discountRule exists if we are setting a type or value with a valid amount
            if (!updatedProduct?.salesPrice?.salesDiscountBreakups[0]?.discountType && (field === 'discountType' || (field === 'discountValue' && parseFloat(value) > 0))) {
                updatedProduct.salesPrice.salesDiscountBreakups[0].discountType = 'percent';
            }
            
            if (field === 'discountType') {
                if (updatedProduct?.salesPrice?.salesDiscountBreakups[0]?.discountType) {
                    updatedProduct.salesPrice.salesDiscountBreakups[0].discountType = value as 'percent' | 'amount';
                }
            } else if (field === 'discountValue') {
                const numValue = parseFloat(value) || 0;
                if (numValue > 0 && updatedProduct?.salesPrice?.salesDiscountBreakups[0]?.discountType) {
                    updatedProduct.salesPrice.salesDiscountBreakups[0].discountValue = numValue;
                } else {
                    // If value is 0 or invalid, remove the discount rule
                    delete updatedProduct?.salesPrice?.salesDiscountBreakups[0]?.discountType;
                }
            }
        }
        
        newCatalog[sku] = updatedProduct;
        return newCatalog;
    });
  }

  calculateSellingPrice(product: Product): number {
      if (!product?.salesPrice?.salesDiscountBreakups || product?.salesPrice?.salesDiscountBreakups[0]?.discountValue <= 0) {
          return product?.salesPrice?.pr_DefaultSalesMRPPrice
      }
      if (product?.salesPrice?.salesDiscountBreakups[0]?.discountType === 'amount') {
          return Math.max(0, product?.salesPrice?.pr_DefaultSalesMRPPrice - product?.salesPrice?.salesDiscountBreakups[0]?.discountValue);
      }
      // It's a percentage
      return product?.salesPrice?.pr_DefaultSalesMRPPrice * (1 - (product?.salesPrice?.salesDiscountBreakups[0]?.discountValue / 100));
  }


  handleGlobalKeydown(event: KeyboardEvent) {
    if (!this.isLoggedIn()) {
      // Support keyboard input on login screen
      const activeEl = document.activeElement as HTMLElement;
      if (activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'SELECT') {
        if (event.key === 'Enter') {
          this.performLogin();
        }
        return;
      }
      if (/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        if (!this.selectedUserForLogin()) {
          const john = this.staffUsers().find(u => u.id === 'USER-001') || this.staffUsers()[0];
          if (john) this.selectUserForLogin(john);
        }
        this.appendAccessCode(event.key);
        return;
      }
      if (event.key === 'Backspace') {
        event.preventDefault();
        this.backspaceAccessCode();
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        this.performLogin();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.deselectUserForLogin();
        return;
      }
      return;
    }
    if (this.isNumPadOpen()) {
        event.stopPropagation(); event.preventDefault();
        if (event.key === 'Escape') { this.closeNumPad(); return; }
        if (event.key === 'Enter') { this.numPadConfirm(); return; }
        if (event.key === 'Backspace') { this.numPadBackspace(); return; }
        if (event.key.toLowerCase() === 'c' || event.key === 'Delete') { this.numPadClear(); return; }
        if (/^[0-9.]$/.test(event.key)) { this.numPadAppend(event.key); return; }
        return;
    }
    if (this.isDiscountModalOpen()) {
        event.stopPropagation();
        if (event.key === 'Escape') { event.preventDefault(); this.closeDiscountModal(); return; }
        if (event.key === 'Enter') { event.preventDefault(); this.confirmDiscount(); return; }
        if (event.key === 'Backspace') { this.backspaceDiscount(); return; }
        if (/^[0-9.]$/.test(event.key)) { this.appendDiscountChar(event.key); return; }
        return;
    }
    if (this.isStockModalOpen()) {
        if (event.key === 'Escape') { event.preventDefault(); this.closeStockModal(); return; }
        const items = this.stockCatalog();
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.stockSelectedIndex.update(i => Math.min(i + 1, items.length - 1));
            document.getElementById('stock-row-' + this.stockSelectedIndex())?.scrollIntoView({ block: 'nearest' });
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.stockSelectedIndex.update(i => Math.max(i - 1, 0));
            document.getElementById('stock-row-' + this.stockSelectedIndex())?.scrollIntoView({ block: 'nearest' });
            return;
        }
        if (event.key === 'Enter' && items.length > 0) {
            event.preventDefault();
            this.addStockItemToCart(items[this.stockSelectedIndex()]);
            return;
        }
        return;
    }
    if (this.isUnitModalOpen()) {
      if (event.key === 'Escape') { event.preventDefault(); this.closeUnitModal(); return; }
      return;
    }
    if (this.isGiftCardModalOpen()) {
        if (event.key === 'Escape') { event.preventDefault(); this.closeGiftCardModal(); return; }
        if (event.key === 'Enter') { event.preventDefault(); this.confirmGiftCard(); return; }
        return;
    }
    if (this.isVoidModalOpen()) {
        if (event.key === 'Escape') { event.preventDefault(); this.closeVoidModal(); return; }
        const bills = this.voidedBills();
        if (bills.length > 0) {
            if (event.key === 'ArrowDown') { event.preventDefault(); this.selectedVoidIndex.update(i => Math.min(i + 1, bills.length - 1)); return; }
            if (event.key === 'ArrowUp') { event.preventDefault(); this.selectedVoidIndex.update(i => Math.max(i - 1, 0)); return; }
            if (event.key === 'Enter') {
                event.preventDefault();
                const selectedBill = bills[this.selectedVoidIndex()];
                if (selectedBill) { this.restoreVoidedBill(selectedBill.id); }
                return;
            }
        }
        return;
    }
    if (this.isHeldBillsModalOpen()) {
        if (event.key === 'Escape') { event.preventDefault(); this.closeHeldBillsModal(); return; }
        const bills = this.heldBills();
        if (bills.length > 0) {
            if (event.key === 'ArrowDown') { event.preventDefault(); this.selectedHeldBillIndex.update(i => Math.min(i + 1, bills.length - 1)); return; }
            if (event.key === 'ArrowUp') { event.preventDefault(); this.selectedHeldBillIndex.update(i => Math.max(i - 1, 0)); return; }
            if (event.key === 'Enter') {
                event.preventDefault();
                const selectedBill = bills[this.selectedHeldBillIndex()];
                if (selectedBill) { this.resumeBill(selectedBill.id); }
                return;
            }
        }
        if (event.altKey && (event.key === 'n' || event.key === 'N')) { event.preventDefault(); this.startNewTab(); return; }
        return;
    }
    if (this.isQtyModalOpen()) {
        event.stopPropagation();
        if (event.key === 'Enter') { event.preventDefault(); this.qtyModalConfirm(); return; }
        if (event.key === 'Escape') { event.preventDefault(); this.qtyModalCancel(); return; }
        if (event.key === 'Backspace') { event.preventDefault(); this.qtyModalBackspace(); return; }
        if (/^[0-9.]$/.test(event.key)) { event.preventDefault(); this.qtyModalAppend(event.key); return; }
        return;
    }
    if (this.isLoyaltyModalOpen() || this.isCustomerModalOpen() || this.isStaffModalOpen()) {
        if (event.key === 'Escape') { 
            event.preventDefault();
            if (this.isCustomerModalOpen()) this.closeCustomerModal();
            else if (this.isStaffModalOpen()) this.closeStaffModal();
            else this.closeLoyaltyModal();
            return;
        }
        if (this.isLoyaltyModalOpen() && event.key === 'Enter' && this.filteredCustomers().length > 0) { this.selectCustomer(this.filteredCustomers()[0]); return; }
        return;
    }
    if (this.isProfileDrawerOpen()) {
        if (event.key === 'Escape') { event.preventDefault(); this.toggleProfileDrawer(); return; }
        return;
    }
    if (this.isSettingsModalOpen()) {
        const activeElement = document.activeElement as HTMLElement;
        const isInputFocused = activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'SELECT';
        if (event.key === 'Escape' && !isInputFocused) { 
            event.preventDefault(); 
            this.closeSettings(); 
            return; 
        }
        if (isInputFocused && event.key === 'Enter') {
            activeElement.blur();
        }
        // Allow other keys to function normally inside settings inputs
        return;
    }
    if (event.altKey && (event.key === 'q' || event.key === 'Q')) { event.preventDefault(); this.actionSetQty(); return; }
    if (event.altKey && (event.key === 'l' || event.key === 'L')) { event.preventDefault(); this.actionLoyalty(); return; }
    if (event.altKey && (event.key === 'u' || event.key === 'U')) { event.preventDefault(); this.actionUnit(); return; }
    if (event.altKey && (event.key === 'r' || event.key === 'R')) { event.preventDefault(); this.actionReturn(); return; }
    if (event.altKey && (event.key === 'g' || event.key === 'G')) { event.preventDefault(); this.actionGiftCard(); return; }
    if (event.altKey && (event.key === 'h' || event.key === 'H')) { event.preventDefault(); this.actionHoldBill(); return; }
    if (event.altKey && (event.key === 'v' || event.key === 'V')) { event.preventDefault(); this.actionVoid(); return; }
    if (event.key === 'F2') { event.preventDefault(); this.searchInput?.nativeElement?.focus(); return; }
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' && target !== this.scannerInput?.nativeElement) { return; }
    const now = Date.now(), delta = now - this.lastKeyTime; this.lastKeyTime = now;
    if (event.key === 'Enter') {
      if (this.scanBuffer.length > 0) { this.processScanOrSearch(this.scanBuffer); this.scanBuffer = ''; }
      return;
    }
    if (delta > 50) this.scanBuffer = '';
    if (event.key.length === 1) this.scanBuffer += event.key;
  }

  // private loadDataGetByID(productId: string | number | null = null, searchContent: string | null = null) {
  //   return this.getAuthContext().pipe(
  //      switchMap(auth => {
  //        const payload = {
  //          subscriberID: auth?.subscriptionId?.toString(),
  //          passKey: auth?.passKey,
  //          masterID: auth?.masterId,
  //          companyID: auth?.companyId?.toString(),
  //          roleID: auth?.masterRoleId,
  //          searchContent: searchContent || null,
  //          Pr_ProductID: productId || null,
  //          fromDate: null,
  //          toDate: null,
  //          pageNumber: 1,
  //          pageSize: this.pageSize(),
  //          SortOrder: "asc",
  //          detailView: true,
  //          pr_CategoryID: null,
  //          pr_RelationID: null,
  //          pr_BrandID: null,
  //          sortOrder: 'asc',
  //        };
   
  //        return this.apiService.postAuthorizedInv<any>(
  //          Global.InvLookUp_EndPoint + 'GetProduct',
  //          payload
  //        );
  //      }),
  //      map(res => {
  //       if (!res?.isSuccess) {
  //          throw new Error(res?.message ?? 'Failed to fetch product details');
  //        }
         
  //        const products: any[] = res.result;
  //        const fetchedProduct = products ? products : null;
  //        this.addToCart(fetchedProduct);
  //   return fetchedProduct;
  //      }),
  //      catchError(err => {
  //       const msg = err?.error?.message === 'BadRequest' 
  //          ? err?.error?.details 
  //          : (err?.error?.message || err?.message);
  //        console.error('Error fetching product by ID/Name:', msg);
  //       return of(null);
  //      })
  //    );
  //  }

   private getAuthContext(): Observable<{
    masterRoleId: any;
    companyId: any;
    masterId: any;
    passKey: any;
    subscriptionId: any;
  }> {
    
    return from(
      Promise.all([
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_MASTER_ROLE_ID),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_COMPANY_ID),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_MASTER_ID),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_PASS_KEY),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_SUBSCRIPTION_ID),
      ])
    ).pipe(
      map(([masterRoleId, companyId, masterId, passKey, subscriptionId]) => {
        if (!masterRoleId || !companyId || !masterId || !passKey || !subscriptionId) {
          throw new Error('Required auth context missing');
        }
        
        return { masterRoleId, companyId, masterId, passKey, subscriptionId };
      })
    );
  }
  private loadStoreGetByID() {
    ;
    return from(
      Promise.all([
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_MASTER_ROLE_ID),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_COMPANY_ID),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_MASTER_ID),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_PASS_KEY),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_SUBSCRIPTION_ID),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_USER_NAME),
      ])
    ).pipe(
      switchMap(([masterRoleId, companyId, masterId, passKey, subscriptionId, email]) => {
        // DEBUG: Check what values are actually decrypted from storage
        console.log('Decrypted Check:', { masterRoleId, companyId, masterId, passKey, subscriptionId, email });
  
        if (!masterRoleId || !companyId || !masterId || !passKey || !subscriptionId || !email) {
          console.error('Required auth context or email missing from storage!');
          throw new Error('Required auth context missing');
        }
  
        const payload = {
          SubscriptionID: subscriptionId?.toString(),
          passKey: passKey,
          MasterID: masterId,
          CompanyID: companyId?.toString(),
          RoleID: masterRoleId,
          EmailID: email || null,
        };
  
        console.log('Calling API with payload:', payload);
  
        return this.apiService.postAuthorizedInvSales<any>(
          Global.CounterSalesInvoice_EndPoint + 'CSIStoreSettings/GetAll',
          payload
        );
      }),
      map(res => {
        console.log('API Response:', res);
        if (!res?.isSuccess) {
          throw new Error(res?.message ?? 'Failed to fetch store details');
        }
        return res.result ? res.result[0] : null;
      }),
      catchError(err => {
        const msg = err?.error?.message === 'BadRequest' 
           ? err?.error?.details 
           : (err?.error?.message || err?.message);
        console.error('Error in loadStoreGetByID:', msg);
        return of(null);
      })
    );
  }

  getLocationList(branchID: string, targetLocationID?: string,text?:any): void {
    const sub = this.getAuthContext().pipe(
      switchMap(auth => {
        const payload = {
          subscriptionID: auth?.subscriptionId.toString(),
          passKey: auth?.passKey,
          masterID: auth?.masterId,
          companyID: auth?.companyId.toString(),
          roleID: auth?.masterRoleId,
          branchID: branchID
        };
  
        return this.apiService.postAuthorizedGen<any>(
          Global.GenLookUp_EndPoint + 'GetCompanyLocations',
          payload
        );
      }),
      map(res => {
        if (!res?.isSuccess) {
          throw new Error(res?.message ?? 'Failed to fetch locations');
        }
        return res.result;
      })
    ).subscribe({
      next: (locs: any[]) => {
        this.locations = locs || [];
      
      if(text == 'warehouse')
      {
        if (targetLocationID) {
          const matchedLocation = this.locations.find(
             l => String(l.id || l.id || l.id) === String(targetLocationID)
           );
   
           if (matchedLocation) {
            const locName = matchedLocation.name;
             if (locName) {
               this.settingDefaultWarehouse.set(locName);
               this.settingDefaultWarehouseID.set(matchedLocation?.id); // Use .set(...) if it's a Signal, or = locName if a regular property
             }
           }
         }
      }
      if(text == 'stocklocation')
        {
          if (targetLocationID) {
            const matchedLocation = this.locations.find(
               l => String(l.id || l.id || l.id) === String(targetLocationID)
             );
     
             if (matchedLocation) {
              const locName = matchedLocation.name;
               if (locName) {
                 this.settingDefaultStockLocation.set(locName); // Use .set(...) if it's a Signal, or = locName if a regular property
                 this.settingDefaultStockLocationID.set(matchedLocation?.id);
                }
             }
           }
        }
        
      },
      error: (err) => {
        console.error('Error fetching locations:', err);
      }
    });
  }
  
    getAccountList(id:any,text:string): void {
      const sub = this.getAuthContext().pipe(
        switchMap(auth => {
          const payload = {
            subscriptionID: auth?.subscriptionId.toString(),
            passKey: auth?.passKey,
            masterID: auth?.masterId,
            companyID: auth?.companyId.toString(),
            roleID: auth?.masterRoleId,
            };
    
          return this.apiService.postAuthorizedAcc<any>(
            Global.AccountsLookUp_EndPoint + 'GetChartOfAccounts',
            payload
          );
        }),
        map(res => {
          if (!res?.isSuccess) {
            throw new Error(res?.message ?? 'Failed to fetch locations');
          }
          return res.result;
        })
      ).subscribe({
        next: (locs: any[]) => {
          this.accountList = locs || [];
          
          if(text == 'defaultCashAccount')
            {
              if (id) {
                const matchedLocation = this.accountList.find(
                   l => String(l.chartOfAccountID || l.id || l.chartOfAccountID) === String(id)
                 );
         
                 if (matchedLocation) {
                  const accountName = matchedLocation.accountName;
                   if (accountName) {
                     this.settingDefaultCashAccount.set(accountName); // Use .set(...) if it's a Signal, or = locName if a regular property
                     this.settingDefaultCashAccountID.set(matchedLocation?.chartOfAccountID);
                    }
                 }
               }
            }
            if(text == 'cashSalesInvoice')
              {
                if (id) {
                  const matchedLocation = this.accountList.find(
                     l => String(l.chartOfAccountID || l.id || l.chartOfAccountID) === String(id)
                   );
           
                   if (matchedLocation) {
                    const accountName = matchedLocation.accountName;
                     if (accountName) {
                       this.settingCashSalesInvoice.set(accountName); // Use .set(...) if it's a Signal, or = locName if a regular property
                       this.settingCashSalesInvoiceID.set(matchedLocation?.accountName);
                      }
                   }
                 }
              }
              if(text == 'defaultSalesAccount')
                {
                  if (id) {
                    const matchedLocation = this.accountList.find(
                       l => String(l.chartOfAccountID || l.id || l.chartOfAccountID) === String(id)
                     );
             
                     if (matchedLocation) {
                      const accountName = matchedLocation.accountName;
                       if (accountName) {
                         this.settingDefaultSalesAccount.set(accountName); // Use .set(...) if it's a Signal, or = locName if a regular property
                         this.settingDefaultSalesAccountID.set(matchedLocation?.chartOfAccountID);
                        }
                     }
                   }
                }
            
         
        },
        error: (err) => {
         console.error('Error fetching locations:', err);
        }
      });
      }


      getPartyAccountAddress(id:any): void {
        const sub = this.getAuthContext().pipe(
         switchMap(auth => {
           const payload = {
             subscriptionID: auth?.subscriptionId.toString(),
             passKey: auth?.passKey,
             masterID: auth?.masterId,
             companyID: auth?.companyId.toString(),
             roleID: auth?.masterRoleId,
             PartyAccountID: id
           };
     
           return this.apiService.postAuthorizedGen<any>(
             Global.GenLookUp_EndPoint + 'getPartyAccountAddresses',
             payload
           );
         }),
         map(res => {
           if (!res?.isSuccess) {
             throw new Error(res?.message ?? 'Failed to fetch party accounts');
           }
           return res.result;
         })
       ).subscribe({
         next: (parties: any[]) => {
          this.addressList = parties || [];
          this.settingPartyState.set(this.addressList[0]?.state);
          console.log('this.settingPartyState',this.settingPartyState())
         },
         error: (err) => {
          console.error('Error fetching party accounts:', err);
         }
       });
     
       
     }
     getBranchAddress(id:any): void {
      const sub = this.getAuthContext().pipe(
       switchMap(auth => {
         const payload = {
           subscriptionID: auth?.subscriptionId.toString(),
           passKey: auth?.passKey,
           masterID: auth?.masterId,
           companyID: auth?.companyId.toString(),
           roleID: auth?.masterRoleId,
           branchID: id
         };
   
         return this.apiService.postAuthorizedGen<any>(
           Global.GenLookUp_EndPoint + 'GetCompanyBranchAddresses',
           payload
         );
       }),
       map(res => {
         if (!res?.isSuccess) {
           throw new Error(res?.message ?? 'Failed to fetch party accounts');
         }
         return res.result;
       })
     ).subscribe({
       next: (branches: any[]) => {
        this.branchAddressList = branches || [];
        this.settingBranchState.set(this.branchAddressList[0]?.state);
        console.log('this.settingPartyState',this.settingPartyState())
       },
       error: (err) => {
        console.error('Error fetching party accounts:', err);
       }
     });
   
     
   }
      
  getPartyAccountList(id:any,defaultPriceList:string): void {
     const sub = this.getAuthContext().pipe(
      switchMap(auth => {
        const payload = {
          subscriptionID: auth?.subscriptionId.toString(),
          passKey: auth?.passKey,
          masterID: auth?.masterId,
          companyID: auth?.companyId.toString(),
          roleID: auth?.masterRoleId
        };
  
        return this.apiService.postAuthorizedGen<any>(
          Global.GenLookUp_EndPoint + 'GetPartyAccounts',
          payload
        );
      }),
      map(res => {
        if (!res?.isSuccess) {
          throw new Error(res?.message ?? 'Failed to fetch party accounts');
        }
        return res.result;
      })
    ).subscribe({
      next: (parties: any[]) => {
        this.customers.set((parties || []).map(p => ({
          ...p,
          partyDisplayName: p.cr_Balance ? `${p.partyName} - Balance: ${p.cr_Balance} ${p.balanceType || 'Dr'}` : p.partyName
        })));
        if (id) {
          const matchedLocation = parties.find(
             l => String(l.partyAccountID) === String(id)
           );
   
           if (matchedLocation) {
            const partyName = matchedLocation.partyName;
             if (partyName) {
               this.settingDefaultCustomer.set(partyName); // Use .set(...) if it's a Signal, or = locName if a regular property
               this.settingDefaultCustomerID.set(matchedLocation?.partyAccountID);
              }
             this.getPartyAccountAddress(matchedLocation.partyAccountID)
             if(defaultPriceList)
             {
              this.getPartyPriceList(matchedLocation.partyAccountID,matchedLocation.partyName,defaultPriceList)
             }
             
           }
         }
       
      },
      error: (err) => {
       console.error('Error fetching party accounts:', err);
      }
    });
  
    
  }
    
    getPartyPriceList(pr_PartyAccountID: string,Pr_PartyAccountName:string,id:any): void {
      const sub = this.getAuthContext().pipe(
        switchMap(auth => {
          const payload = {
            SubscriberID: auth?.subscriptionId.toString(),
            passKey: auth?.passKey,
            masterID: auth?.masterId,
            companyID: auth?.companyId.toString(),
            roleID: auth?.masterRoleId,
            pr_PartyAccountID: pr_PartyAccountID,
            Pr_PartyAccountName : Pr_PartyAccountName
          };
    
          return this.apiService.postAuthorizedInv<any>(
            Global.GenLookUp_EndPoint + 'GetPartyPriceIDs',
            payload
          );
        }),
        map(res => {
          if (!res?.isSuccess) {
            throw new Error(res?.message ?? 'Failed to fetch locations');
          }
          return res.result;
        })
      ).subscribe({
        next: (locs: any[]) => {
        this.partyPriceList = locs || [];
        if (id) {
          const matchedLocation = this.partyPriceList.find(
             l => String(l.priceID) === String(id)
           );
   
           if (matchedLocation) {
            const priceTitle = matchedLocation.priceTitle;
             if (priceTitle) {
               this.settingDefaultPriceList.set(priceTitle); // Use .set(...) if it's a Signal, or = locName if a regular property
               this.settingDefaultPriceListID.set(matchedLocation?.priceID);
              }
             }
         }
         
        },
        error: (err) => {
         console.error('Error fetching locations:', err);
        }
      });
      }
      getTaxTypeList(id:any): void {
        const sub = this.getAuthContext().pipe(
          switchMap(auth => {
            const payload = {
              subscriptionID: auth?.subscriptionId.toString(),
              passKey: auth?.passKey,
              masterID: auth?.masterId,
              companyID: auth?.companyId.toString(),
              roleID: auth?.masterRoleId
            };
      
            return this.apiService.postAuthorizedGen<any>(
              Global.GenLookUp_EndPoint + 'GenTaxType',
              payload
            );
          }),
          map(res => {
            if (!res?.isSuccess) {
              throw new Error(res?.message ?? 'Failed to fetch tax types');
            }
            return res.result;
          })
        ).subscribe({
          next: (taxTypes: any[]) => {
            this.taxTypesList = taxTypes || [];
      
      if (id) {
        const matchedLocation = this.taxTypesList.find(
           l => String(l.id) === String(id)
         );
 
         if (matchedLocation) {
          const label = matchedLocation.label;
           if (label) {
             this.settingDefaultTaxCategory.set(label); // Use .set(...) if it's a Signal, or = locName if a regular property
             this.settingDefaultTaxCategoryID.set(matchedLocation?.id);
            }
           }
       }
           
          },
          error: (err) => {
           console.error('Error fetching tax types:', err);
          }
        });
      
        
      }
      
      getTaxRateList(taxTypeId: string): void {
        const sub = this.getAuthContext().pipe(
          switchMap(auth => {
            const payload = {
              subscriptionID: auth?.subscriptionId.toString(),
              passKey: auth?.passKey,
              masterID: auth?.masterId,
              companyID: auth?.companyId.toString(),
              roleID: auth?.masterRoleId,
              TaxTypeId: taxTypeId
            };
      
            return this.apiService.postAuthorizedGen<any>(
              Global.GenLookUp_EndPoint + 'GenTaxRate',
              payload
            );
          }),
          map(res => {
            if (!res?.isSuccess) {
              throw new Error(res?.message ?? 'Failed to fetch tax rates');
            }
            return res.result;
          })
        ).subscribe({
          next: (rates: any[]) => {
            this.taxRatesList = (rates || []).map(rate => ({
              ...rate,
              displayRate: `${rate.taxTypeRate}%`
            }));
           
          },
          error: (err) => {
            console.error('Error fetching tax rates:', err);
            this.taxRatesList = [];
           
          }
        });
      
        
      }

      
}
